import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import Logo from '../components/Logo'
import MarkdownRenderer from '../components/MarkdownRenderer'
import { useAuth } from '../contexts/AuthContext'
import HomeworkAdminPanel from '../components/HomeworkAdminPanel'
import CodeEditor from '../components/CodeEditor'
import GateToolbar from '../components/circuit/GateToolbar'
import CircuitCanvas from '../components/circuit/CircuitCanvas'
import ParameterModal, { QubitSelectionModal, ConditionalGateModal } from '../components/circuit/ParameterModal'
import { Circuit, GateType, PlacedGate, GateCondition, GATE_DEFINITIONS, createEmptyCircuit, generateGateId } from '../types/circuit'
import { compileToQiskit, decompileToCircuit } from '../utils/circuitCompiler'
import { validateCircuitCode, extractMeasurementsFromCode, extractMeasurementsFromCircuit, validatePostSelectLength } from '../utils/codeValidator'
import {
  homeworkApi,
  challengeApi,
  type HomeworkInfo,
  type HomeworkPublicInfo,
  type HomeworkSubmissionResult,
  type HomeworkQueueStatus,
  type HomeworkSimulateResult,
  type CheckTranspileResult,
  type FakeHardwareSubmitResult,
  type ToffoliSimulateResult,
  type ToffoliSubmitResult,
  type ToffoliHardwareSubmitResult,
  type ToffoliTranspileResult,
} from '../utils/api'
import { findProviderByBackend, HARDWARE_PROVIDERS } from '../data/hardwareProviders'
import { getCalibrationData } from '../data/hardwareCalibrationData'
import HardwareTopology from '../components/HardwareTopology'

const STARTER_CODE = `# Entanglement Distillation — Starter Example (BBPSSW)
#
# GOAL: Achieve the highest fidelity Bell pair on qubits 0 and 1.
#   You are NOT limited to BBPSSW! Feel free to design any distillation
#   protocol you like — use more qubits, different gate sequences, creative
#   post-selection strategies, or entirely new approaches. The only thing
#   that matters is the fidelity of the output Bell pair.
#
# RULES:
#   - Output Bell pair is ALWAYS on qubits 0 and 1.
#   - Ancilla qubits: 2, 3, ... (used for distillation).
#   - You MUST measure ALL qubits.
#   - Fidelity is measured via Inverse Bell or Tomography (choose above).
#
# POST-SELECTION:
#   POST_SELECT = set of bitstrings to filter ancilla measurements.
#   Only shots where the ancilla bits match a string in POST_SELECT are kept.
#   If POST_SELECT is not defined, all shots are kept (no filtering).
#
#   STRING LENGTH:
#     Must equal the number of ancilla measurements (all measurements
#     except q0 and q1, which are measured by the fidelity test).
#
#   BIT ORDERING (Qiskit convention):
#     Follows Qiskit's bit ordering: highest classical bit index FIRST (leftmost).
#     POST_SELECT string = c[n-1] c[n-2] ... c[2]  (left-to-right)
#     Example below: ancilla q2->c2, q3->c3
#       POST_SELECT positions: [c3, c2]
#       {"00"} = c3=0, c2=0   |   {"01"} = c3=0, c2=1
#       {"10"} = c3=1, c2=0   |   {"11"} = c3=1, c2=1
#
# QUBIT LAYOUT (required for Hardware / FakeHardware, not needed for Simulator):
#   INITIAL_LAYOUT = [physical_qubit_for_q0, physical_qubit_for_q1, ...]
#   Maps your logical qubits to specific physical qubits on the hardware.
#   Check the hardware topology (click info button) to pick low-error qubits.
#   Example: INITIAL_LAYOUT = [0, 1, 2, 3]
#
# --- Example: BBPSSW Protocol (you can replace this entirely!) ---
#   1. Prepare two Bell pairs: (q0,q1) and (q2,q3)
#   2. Bilateral CNOT from pair 1 to pair 2
#   3. Measure all qubits
#   4. Post-select on ancilla = "00" (c3=0, c2=0)

qc = QuantumCircuit(4, 4)

# Bell pair 1 (output): q0, q1
qc.h(0)
qc.cx(0, 1)

# Bell pair 2 (ancilla): q2, q3
qc.h(2)
qc.cx(2, 3)

# TODO: Implement bilateral CNOT between pair 1 and pair 2

# Measure ALL qubits (q0->c0, q1->c1, q2->c2, q3->c3)
qc.measure([0, 1, 2, 3], [0, 1, 2, 3])

# TODO: Define POST_SELECT to filter ancilla measurements
# POST_SELECT = {"00"}
`

const TOFFOLI_STARTER_CODE = `# Approximate Toffoli — Starter Template
#
# GOAL: Build a circuit as close to the Toffoli gate as possible.
#
# QUBITS:
#   q0 = A (control qubit)
#   q1 = B (control qubit)
#   q2 = C (target qubit)
#
# The ideal Toffoli gate flips C when both A and B are |1>.
#
# CONSTRAINTS:
#   - Exactly 3 qubits (A, B, C)
#   - Any number of CX (CNOT) gates
#   - Any number of 1-qubit gates
#   - No other multi-qubit gates
#   - Linear connectivity: A(0) — B(1) — C(2)
#     CX gates only between adjacent qubits: (0,1) or (1,2)
#
# EVALUATION:
#   Your circuit's unitary is compared to the ideal Toffoli via
#   Hilbert-Schmidt distance. No measurements needed.
#
# AVAILABLE 1-QUBIT GATES:
#   qc.h(qubit)              # Hadamard
#   qc.x(qubit)              # Pauli-X
#   qc.y(qubit)              # Pauli-Y
#   qc.z(qubit)              # Pauli-Z
#   qc.s(qubit)              # S gate (sqrt-Z)
#   qc.sdg(qubit)            # S-dagger
#   qc.t(qubit)              # T gate (pi/8)
#   qc.tdg(qubit)            # T-dagger
#   qc.rx(angle, qubit)      # Rotation around X
#   qc.ry(angle, qubit)      # Rotation around Y
#   qc.rz(angle, qubit)      # Rotation around Z
#   qc.p(angle, qubit)       # Phase gate
#   qc.u(theta, phi, lam, qubit)  # General U gate
#   qc.sx(qubit)             # Sqrt-X
#
#   Angles can use: pi, sqrt(), e.g. pi/4, 2*pi/3
#
# 2-QUBIT GATE (adjacent qubits only):
#   qc.cx(control, target)   # CNOT: (0,1), (1,0), (1,2), or (2,1)

# 3 qubits: A(0)=control, B(1)=control, C(2)=target
qc = QuantumCircuit(3)

# --- Your circuit here ---
# Add 1-qubit gates between CX gates to approximate Toffoli.
# Example: bare CX gates (HS distance ~ 0.66)

qc.cx(0, 1)   # A-B
qc.cx(1, 2)   # B-C
qc.cx(0, 1)   # A-B
qc.cx(1, 2)   # B-C
qc.cx(0, 1)   # A-B
qc.cx(1, 2)   # B-C
qc.cx(0, 1)   # A-B

# DO NOT add measurements — the backend computes the unitary directly.
`

type EditorMode = 'code' | 'composer'

interface HomeworkPageProps {
  challengeMode?: boolean
}

function HomeworkPage({ challengeMode = false }: HomeworkPageProps) {
  const params = useParams<{ homeworkId: string; challengeId: string }>()
  const homeworkId = challengeMode ? params.challengeId : params.homeworkId
  const { user } = useAuth()
  const isAdmin = user?.is_admin === true
  const [showAdmin, setShowAdmin] = useState(false)

  // Public homework info (loaded without token)
  const [publicInfo, setPublicInfo] = useState<HomeworkPublicInfo | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  // Token state
  const [token, setToken] = useState('')
  const [isVerifying, setIsVerifying] = useState(false)
  const [homeworkInfo, setHomeworkInfo] = useState<HomeworkInfo | null>(null)

  // Code & editor state — initialize from localStorage draft if available
  const [code, setCode] = useState(() => {
    const draft = localStorage.getItem(`hw_draft_code_${homeworkId}`)
    return draft ?? STARTER_CODE
  })
  const [codeSaved, setCodeSaved] = useState(true)
  const [editorMode, setEditorMode] = useState<EditorMode>('code')
  const [selectedBackend, setSelectedBackend] = useState('')
  const [shots, setShots] = useState(1024)
  const [selectedLanguage, setSelectedLanguage] = useState('qiskit')
  const [evalMethod, setEvalMethod] = useState<'inverse_bell' | 'tomography'>('inverse_bell')
  const [postSelect, setPostSelect] = useState('{"00"}')
  const [initialLayout, setInitialLayout] = useState('')
  const [parseWarning, setParseWarning] = useState<string | null>(null)

  // Optional student-provided IBM API key and instance (stored in localStorage only)
  const [customApiKey, setCustomApiKey] = useState(() =>
    localStorage.getItem(`hw_custom_apikey_${homeworkId}`) || ''
  )
  // Circuit composer state
  const [circuit, setCircuit] = useState<Circuit>(createEmptyCircuit(4))
  const [draggingGate, setDraggingGate] = useState<GateType | null>(null)
  const [selectedGates, setSelectedGates] = useState<Set<string>>(new Set())

  // Modal states for composer
  const [paramModal, setParamModal] = useState<{
    isOpen: boolean; gateType: GateType | null; qubitIndex: number; column: number
  }>({ isOpen: false, gateType: null, qubitIndex: 0, column: 0 })
  const [qubitModal, setQubitModal] = useState<{
    isOpen: boolean; gateType: GateType | null; column: number; pendingParams?: number[]
  }>({ isOpen: false, gateType: null, column: 0 })
  const [conditionalModal, setConditionalModal] = useState<{
    isOpen: boolean; qubitIndex: number; column: number
  }>({ isOpen: false, qubitIndex: 0, column: 0 })

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [showSubmitModal, setShowSubmitModal] = useState(false)
  const [submitKeyChoice, setSubmitKeyChoice] = useState<'platform' | 'own'>('platform')

  // Simulator state
  const [isSimulating, setIsSimulating] = useState(false)
  const [simulatorResult, setSimulatorResult] = useState<HomeworkSimulateResult | null>(null)
  const [simulateError, setSimulateError] = useState<string | null>(null)
  const [simulateMode, setSimulateMode] = useState<'distillation' | 'bell_pair'>('distillation')
  const [singleQubitError, setSingleQubitError] = useState(0.01)
  const [twoQubitError, setTwoQubitError] = useState(0.02)

  // Fake hardware state
  const [isSubmittingFakeHw, setIsSubmittingFakeHw] = useState(false)
  const [fakeHwResult, setFakeHwResult] = useState<FakeHardwareSubmitResult | null>(null)
  const [fakeHwError, setFakeHwError] = useState<string | null>(null)

  // Toffoli state
  const [toffoliResult, setToffoliResult] = useState<ToffoliSimulateResult | null>(null)
  const [toffoliSubmitResult, setToffoliSubmitResult] = useState<ToffoliSubmitResult | null>(null)
  const [isSimulatingToffoli, setIsSimulatingToffoli] = useState(false)
  const [isSubmittingToffoli, setIsSubmittingToffoli] = useState(false)
  const [toffoliError, setToffoliError] = useState<string | null>(null)

  // Toffoli hardware state
  const [toffoliHwResult, setToffoliHwResult] = useState<ToffoliHardwareSubmitResult | null>(null)
  const [isSubmittingToffoliHw, setIsSubmittingToffoliHw] = useState(false)
  const [toffoliHwError, setToffoliHwError] = useState<string | null>(null)

  // Toffoli IBM hardware state
  const [isSubmittingToffoliIbm, setIsSubmittingToffoliIbm] = useState(false)
  const [toffoliIbmError, setToffoliIbmError] = useState<string | null>(null)
  const [toffoliIbmResult, setToffoliIbmResult] = useState<any>(null)
  const [toffoliBackend, setToffoliBackend] = useState('hs_calculator')
  const [toffoliShots, setToffoliShots] = useState(1024)

  // Queue & history state
  const [queueStatus, setQueueStatus] = useState<HomeworkQueueStatus | null>(null)
  const [submissions, setSubmissions] = useState<HomeworkSubmissionResult[]>([])

  // Sidebar collapse state
  const [showRefCircuit, setShowRefCircuit] = useState(false)

  // Sidebar resize state
  const [sidebarWidth, setSidebarWidth] = useState(288)
  const isResizing = useRef(false)

  // Student profile (display name on leaderboard)
  const [displayName, setDisplayName] = useState('')
  const [methodName, setMethodName] = useState('')
  const [profileSaving, setProfileSaving] = useState(false)
  const [profileSaved, setProfileSaved] = useState(false)

  // Hardware info modal
  const [showHardwareInfo, setShowHardwareInfo] = useState(false)
  // Preview compiled circuit modal
  const [showPreviewCircuit, setShowPreviewCircuit] = useState(false)
  // Check transpiled code modal
  const [showTranspileCheck, setShowTranspileCheck] = useState(false)
  const [transpileResult, setTranspileResult] = useState<CheckTranspileResult | null>(null)
  const [isCheckingTranspile, setIsCheckingTranspile] = useState(false)
  // Toffoli transpile modal
  const [showToffoliTranspile, setShowToffoliTranspile] = useState(false)
  const [toffoliTranspileResult, setToffoliTranspileResult] = useState<ToffoliTranspileResult | null>(null)
  const [isCheckingToffoliTranspile, setIsCheckingToffoliTranspile] = useState(false)
  const [toffoliTranspileInput, setToffoliTranspileInput] = useState<string>('00')

  // ============ API adapter: challenge mode uses challengeApi with homework-compatible shapes ============

  const api = useMemo(() => {
    if (!challengeMode) return homeworkApi

    // Wrap challengeApi to return homework-compatible shapes
    return {
      getInfo: (id: string) =>
        challengeApi.getInfo(id).then((info): HomeworkPublicInfo => ({
          id: info.id,
          title: info.title,
          description: info.description || '',
          course: '',
          homework_type: 'distillation',
          is_active: info.is_active,
          allowed_backends: info.allowed_backends,
          language_whitelist: info.language_whitelist || ['qiskit'],
          deadline: info.deadline,
          starter_code: info.starter_code,
          total_participants: 0,
        })),

      verifyToken: (_token: string) =>
        // In challenge mode, auto-provision token from JWT session (ignore manual token)
        challengeApi.getAuthToken(homeworkId!).then((info): HomeworkInfo => ({
          valid: true,
          homework_id: info.challenge_id,
          homework_title: '',
          course: '',
          budget_total_seconds: info.budget_total_seconds,
          budget_used_seconds: info.budget_used_seconds,
          budget_remaining_seconds: info.budget_remaining_seconds,
          submission_count: info.submission_count,
          display_name: info.display_name,
          homework_type: 'distillation',
          hw_submissions_used: (info as any).hw_submissions_used ?? 0,
          hw_submissions_max: (info as any).hw_submissions_max ?? 3,
          hw_submissions_remaining: (info as any).hw_submissions_remaining ?? 3,
        })).catch((): HomeworkInfo => ({
          valid: false,
          error: 'Please log in to your account first',
        })),

      getQueueStatus: (id: string, token?: string) =>
        challengeApi.getQueueStatus(id, token).then((q): HomeworkQueueStatus => ({
          queue: q.queue.map(e => ({
            id: e.id,
            student_label: e.participant_label,
            position: e.position,
            backend: e.backend,
            submitted_at: e.submitted_at,
          })),
          running: q.running.map(e => ({
            id: e.id,
            student_label: e.participant_label,
            backend: e.backend,
            started_at: e.started_at || undefined,
          })),
          my_submissions: q.my_submissions.map(e => ({
            id: e.id,
            position: e.position,
            status: e.status,
          })),
          total_queued: q.total_queued,
          total_running: q.total_running,
          estimated_wait_minutes: q.estimated_wait_minutes,
        })),

      getSubmissions: (token: string) =>
        challengeApi.getSubmissions(token, homeworkId!).then(r => ({
          submissions: r.submissions.map((s): HomeworkSubmissionResult => ({
            id: s.id,
            homework_id: s.challenge_id,
            status: s.status,
            queue_position: s.queue_position,
            backend_name: s.backend_name,
            shots: s.shots,
            fidelity_after: s.score,
            measurements_after: s.measurements ?? undefined,
            qubit_count: s.qubit_count,
            gate_count: s.gate_count,
            circuit_depth: s.circuit_depth,
            execution_time_seconds: s.execution_time_seconds,
            error_message: s.error_message,
            created_at: s.created_at,
            started_at: s.started_at,
            completed_at: s.completed_at,
          })),
          total: r.total,
        })),

      submit: (data: any) =>
        challengeApi.authSubmit({
          challenge_id: homeworkId!,
          code: data.code,
          backend: data.backend,
          shots: data.shots,
          language: selectedLanguage,
          ibmq_api_key: data.ibmq_api_key || undefined,
        }),

      simulate: (data: any) =>
        challengeApi.authSimulate({
          challenge_id: data.homework_id,
          code: data.code,
          shots: data.shots,
          single_qubit_error: data.single_qubit_error,
          two_qubit_error: data.two_qubit_error,
          language: selectedLanguage,
          target_type: 'simulator',
        }).then((r): HomeworkSimulateResult => ({
          success: r.success,
          error: r.error,
          fidelity_after: r.score ?? undefined,
          qubit_count: r.qubit_count ?? undefined,
          gate_count: r.gate_count ?? undefined,
          circuit_depth: r.circuit_depth ?? undefined,
          execution_time_ms: r.execution_time_ms ?? undefined,
          eval_method: 'custom',
          measurements_after: r.measurements ?? undefined,
          backend: r.backend,
        })),

      submitFakeHardware: (data: any) =>
        challengeApi.authSimulate({
          challenge_id: data.homework_id,
          code: data.code,
          shots: data.shots,
          language: selectedLanguage,
          target_type: 'fake_hardware',
        }).then((r): FakeHardwareSubmitResult => ({
          success: r.success,
          error: r.error,
          fidelity_after: r.score ?? undefined,
          qubit_count: r.qubit_count ?? undefined,
          gate_count: r.gate_count ?? undefined,
          circuit_depth: r.circuit_depth ?? undefined,
          execution_time_ms: r.execution_time_ms ?? undefined,
          measurements: r.measurements ?? undefined,
          eval_method: 'custom',
          backend: r.backend,
        })),

      updateProfile: (data: any) =>
        challengeApi.updateProfile({
          token: data.token,
          display_name: data.display_name,
          method_name: data.method_name,
        }),

      checkTranspile: (data: any) =>
        fetch('/api/challenge/check-transpile', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('qcloud_auth_token')}`,
          },
          body: JSON.stringify({
            challenge_id: homeworkId,
            code: data.code,
            backend_name: data.backend_name,
            language: selectedLanguage,
          }),
        }).then(r => r.json()) as Promise<CheckTranspileResult>,
    }
  }, [challengeMode, homeworkId, selectedLanguage])

  // ============ Load public info on mount ============

  useEffect(() => {
    if (!homeworkId) return

    // Load public homework info (no token needed)
    api.getInfo(homeworkId)
      .then(info => {
        setPublicInfo(info)
        if (HARDWARE_PROVIDERS.length > 0) {
          setSelectedBackend(HARDWARE_PROVIDERS[0].backendName)
        }
      })
      .catch(() => setLoadError('Homework not found'))

    // Try to restore token from localStorage
    const savedToken = localStorage.getItem(`hw_token_${homeworkId}`)
    if (savedToken) {
      setToken(savedToken)
      handleVerifyToken(savedToken)
    }

    // Check for code loaded from job history
    const loadedCode = localStorage.getItem(`hw_load_code_${homeworkId}`)
    if (loadedCode) {
      setCode(loadedCode)
      localStorage.removeItem(`hw_load_code_${homeworkId}`)
    }

    // Fetch queue status (public)
    refreshQueue()
  }, [homeworkId])

  // Set Toffoli starter code and circuit when homework type is detected
  useEffect(() => {
    if (publicInfo?.homework_type === 'toffoli') {
      const draft = localStorage.getItem(`hw_draft_code_${homeworkId}`)
      // If no draft, or draft is clearly from distillation (not Toffoli code), use Toffoli starter
      if (!draft || draft.includes('QuantumCircuit(4') || draft.includes('POST_SELECT')) {
        setCode(TOFFOLI_STARTER_CODE)
        localStorage.removeItem(`hw_draft_code_${homeworkId}`)
      }
      // Lock circuit composer to 3 qubits for Toffoli
      setCircuit(createEmptyCircuit(3))
    }
  }, [publicInfo?.homework_type, homeworkId])

  // In challenge mode, auto-connect when user is logged in
  useEffect(() => {
    if (!challengeMode || !homeworkId || !user) return
    if (isTokenVerified) return // already connected
    handleVerifyToken()
  }, [challengeMode, homeworkId, user])

  // Set challenge starter code when publicInfo loads in challenge mode
  useEffect(() => {
    if (!challengeMode || !publicInfo?.starter_code) return
    const draft = localStorage.getItem(`hw_draft_code_${homeworkId}`)
    if (!draft) {
      setCode(publicInfo.starter_code)
    }
  }, [challengeMode, publicInfo?.starter_code, homeworkId])

  // Derived: is this a Toffoli homework?
  const isToffoli = publicInfo?.homework_type === 'toffoli' || homeworkInfo?.homework_type === 'toffoli'

  // URL prefix for links (homework vs challenge)
  const baseUrl = challengeMode ? `/challenge/${homeworkId}` : `/homework/${homeworkId}`

  // Auto-refresh queue and submissions every 10 seconds
  useEffect(() => {
    if (!homeworkId) return
    const interval = setInterval(() => {
      refreshQueue()
      if (homeworkInfo?.valid && token) {
        refreshSubmissions()
      }
    }, 10000)
    return () => clearInterval(interval)
  }, [homeworkId, homeworkInfo?.valid, token])

  // Auto-save code to localStorage (debounced)
  useEffect(() => {
    if (!homeworkId) return
    setCodeSaved(false)
    const timer = setTimeout(() => {
      localStorage.setItem(`hw_draft_code_${homeworkId}`, code)
      setCodeSaved(true)
    }, 500)
    return () => clearTimeout(timer)
  }, [code, homeworkId])

  // ============ Token / API handlers ============

  const handleVerifyToken = async (tokenValue?: string) => {
    const t = tokenValue || token
    // In challenge mode, token is not needed (JWT auth is used instead)
    if (!challengeMode && !t.trim()) return
    setIsVerifying(true)
    try {
      const info = await api.verifyToken(t)
      if (info.valid) {
        setHomeworkInfo(info)
        setDisplayName(info.display_name || '')
        setMethodName(info.method_name || '')
        if (homeworkId) {
          localStorage.setItem(`hw_token_${homeworkId}`, t)
        }
        refreshSubmissions(t)
      } else {
        setHomeworkInfo({ valid: false, error: info.error || 'Invalid token' })
      }
    } catch {
      setHomeworkInfo({ valid: false, error: 'Failed to verify token' })
    } finally {
      setIsVerifying(false)
    }
  }

  const refreshQueue = useCallback(async (t?: string) => {
    if (!homeworkId) return
    try {
      const status = await api.getQueueStatus(homeworkId, t || token || undefined)
      setQueueStatus(status)
    } catch { /* ignore */ }
  }, [homeworkId, token])

  const refreshSubmissions = useCallback(async (t?: string) => {
    const tokenToUse = t || token
    if (!tokenToUse) return
    try {
      const result = await api.getSubmissions(tokenToUse)
      setSubmissions(result.submissions)
    } catch { /* ignore */ }
  }, [token])

  const openSubmitModal = () => {
    // Pre-select based on whether student has a custom key
    setSubmitKeyChoice(customApiKey.trim() ? 'own' : 'platform')
    setShowSubmitModal(true)
  }

  const handleSubmitConfirm = async () => {
    setShowSubmitModal(false)
    setIsSubmitting(true)
    setSubmitError(null)
    try {
      const codeToSubmit = editorMode === 'composer' ? compileToQiskit(circuit, postSelect, initialLayout || undefined) : code
      const validation = validateCircuitCode(codeToSubmit)
      if (!validation.valid) {
        setSubmitError('Code validation failed:\n' + validation.errors.join('\n'))
        setIsSubmitting(false)
        return
      }
      // INITIAL_LAYOUT is required for hardware submissions
      const hwLayoutMatch = codeToSubmit.match(/^[^#\n]*INITIAL_LAYOUT\s*=\s*\[([^\]]*)\]/m)
      if (!hwLayoutMatch || !hwLayoutMatch[1].trim()) {
        setSubmitError('INITIAL_LAYOUT is required for hardware submissions. Define it in your code, e.g.:\nINITIAL_LAYOUT = [0, 1, 2, 3]')
        setIsSubmitting(false)
        return
      }
      // In challenge mode: check per-user submission limit if using platform key
      if (challengeMode && !customApiKey.trim()) {
        const remaining = homeworkInfo?.hw_submissions_remaining ?? 0
        if (remaining <= 0) {
          setSubmitError(`You have used all ${homeworkInfo?.hw_submissions_max ?? 3} platform hardware submissions for this challenge. Enter your own IBM API key in the sidebar for unlimited submissions.`)
          setIsSubmitting(false)
          return
        }
      }
      const useOwnKey = customApiKey.trim() ? true : false
      await api.submit({
        token,
        code: codeToSubmit,
        backend: selectedBackend,
        shots,
        eval_method: evalMethod,
        ibmq_api_key: useOwnKey ? customApiKey.trim() : undefined,
      })
      refreshQueue()
      refreshSubmissions()
    } catch (err: any) {
      setSubmitError(err.detail || err.message || 'Submission failed')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSimulate = async (mode: 'distillation' | 'bell_pair' = 'distillation') => {
    if (!homeworkId) return
    setIsSimulating(true)
    setSimulateError(null)
    setSimulatorResult(null)
    setSimulateMode(mode)
    try {
      const codeToRun = editorMode === 'composer' ? compileToQiskit(circuit, postSelect, initialLayout || undefined) : code
      const validation = validateCircuitCode(codeToRun)
      if (!validation.valid) {
        setSimulateError('Code validation failed:\n' + validation.errors.join('\n'))
        setIsSimulating(false)
        return
      }
      const result = await api.simulate({
        homework_id: homeworkId,
        code: codeToRun,
        shots,
        mode,
        eval_method: evalMethod,
        single_qubit_error: singleQubitError,
        two_qubit_error: twoQubitError,
      })
      setSimulatorResult(result)
    } catch (err: any) {
      setSimulateError(err.detail || err.message || 'Simulation failed')
    } finally {
      setIsSimulating(false)
    }
  }

  const handleSubmitFakeHardware = async () => {
    if (!homeworkId || (!token && !isAdmin)) return
    setIsSubmittingFakeHw(true)
    setFakeHwError(null)
    setFakeHwResult(null)
    try {
      const codeToRun = editorMode === 'composer' ? compileToQiskit(circuit, postSelect, initialLayout || undefined) : code
      const validation = validateCircuitCode(codeToRun)
      if (!validation.valid) {
        setFakeHwError('Code validation failed:\n' + validation.errors.join('\n'))
        setIsSubmittingFakeHw(false)
        return
      }
      // INITIAL_LAYOUT is required for fake hardware submissions
      const layoutMatch = codeToRun.match(/^[^#\n]*INITIAL_LAYOUT\s*=\s*\[([^\]]*)\]/m)
      if (!layoutMatch || !layoutMatch[1].trim()) {
        setFakeHwError('INITIAL_LAYOUT is required for fake hardware submissions. Define it in your code, e.g.:\nINITIAL_LAYOUT = [0, 1, 2, 3]')
        setIsSubmittingFakeHw(false)
        return
      }
      // Validate INITIAL_LAYOUT length matches qubit count
      const layoutNums = layoutMatch[1].split(',').map(s => s.trim()).filter(s => /^\d+$/.test(s))
      const qcMatch = codeToRun.match(/QuantumCircuit\(\s*(\d+)/)
      const numQubits = qcMatch ? parseInt(qcMatch[1]) : (editorMode === 'composer' ? circuit.numQubits : 0)
      if (numQubits > 0 && layoutNums.length > 0 && layoutNums.length !== numQubits) {
        setFakeHwError(`INITIAL_LAYOUT has ${layoutNums.length} entries but circuit has ${numQubits} qubits. They must match.`)
        setIsSubmittingFakeHw(false)
        return
      }
      const result = await api.submitFakeHardware({
        token: token || undefined,
        homework_id: homeworkId,
        code: codeToRun,
        shots,
        eval_method: evalMethod,
      })
      setFakeHwResult(result)
    } catch (err: any) {
      setFakeHwError(err.detail || err.message || 'Fake hardware submission failed')
    } finally {
      setIsSubmittingFakeHw(false)
    }
  }

  // ============ Toffoli Handlers ============

  const handleToffoliSimulate = async () => {
    if (!homeworkId) return
    setIsSimulatingToffoli(true)
    setToffoliError(null)
    setToffoliResult(null)
    try {
      const codeToRun = editorMode === 'composer' ? compileToQiskit(circuit, undefined, undefined, { noClassicalBits: true }) : code
      const result = await homeworkApi.simulateToffoli({ homework_id: homeworkId, code: codeToRun })
      setToffoliResult(result)
    } catch (err: any) {
      setToffoliError(err.detail || err.message || 'Evaluation failed')
    } finally {
      setIsSimulatingToffoli(false)
    }
  }

  const handleToffoliSubmit = async () => {
    if (!homeworkId || (!token && !isAdmin)) return
    setIsSubmittingToffoli(true)
    setToffoliError(null)
    setToffoliSubmitResult(null)
    try {
      const codeToRun = editorMode === 'composer' ? compileToQiskit(circuit, undefined, undefined, { noClassicalBits: true }) : code
      const result = await homeworkApi.submitToffoli({
        token: token || undefined,
        homework_id: homeworkId,
        code: codeToRun,
      })
      setToffoliSubmitResult(result)
    } catch (err: any) {
      setToffoliError(err.detail || err.message || 'Submission failed')
    } finally {
      setIsSubmittingToffoli(false)
    }
  }

  const handleToffoliHardwareSubmit = async () => {
    if (!homeworkId || (!token && !isAdmin)) return
    setIsSubmittingToffoliHw(true)
    setToffoliHwError(null)
    setToffoliHwResult(null)
    try {
      const codeToRun = editorMode === 'composer' ? compileToQiskit(circuit, undefined, undefined, { noClassicalBits: true }) : code
      // Extract INITIAL_LAYOUT from code
      const layoutMatch = codeToRun.match(/^[^#\n]*INITIAL_LAYOUT\s*=\s*\[([^\]]*)\]/m)
      if (!layoutMatch || !layoutMatch[1].trim()) {
        setToffoliHwError('INITIAL_LAYOUT is required for hardware submissions. Define it in your code, e.g.:\nINITIAL_LAYOUT = [0, 1, 2]')
        setIsSubmittingToffoliHw(false)
        return
      }
      const layoutNums = layoutMatch[1].split(',').map(s => s.trim()).filter(s => /^\d+$/.test(s))
      if (layoutNums.length !== 3) {
        setToffoliHwError(`INITIAL_LAYOUT must have exactly 3 entries for Toffoli (got ${layoutNums.length}). Example: INITIAL_LAYOUT = [0, 1, 2]`)
        setIsSubmittingToffoliHw(false)
        return
      }
      const result = await homeworkApi.submitToffoliHardware({
        token: token || undefined,
        homework_id: homeworkId,
        code: codeToRun,
        shots: toffoliShots,
      })
      setToffoliHwResult(result)
    } catch (err: any) {
      setToffoliHwError(err.detail || err.message || 'Hardware submission failed')
    } finally {
      setIsSubmittingToffoliHw(false)
    }
  }

  const handleToffoliIbmSubmit = async () => {
    if (!homeworkId || (!token && !isAdmin)) return
    setIsSubmittingToffoliIbm(true)
    setToffoliIbmError(null)
    setToffoliIbmResult(null)
    try {
      const codeToRun = editorMode === 'composer' ? compileToQiskit(circuit, undefined, undefined, { noClassicalBits: true }) : code
      const layoutMatch = codeToRun.match(/^[^#\n]*INITIAL_LAYOUT\s*=\s*\[([^\]]*)\]/m)
      if (!layoutMatch || !layoutMatch[1].trim()) {
        setToffoliIbmError('INITIAL_LAYOUT is required for IBM submissions')
        setIsSubmittingToffoliIbm(false)
        return
      }
      const layoutNums = layoutMatch[1].split(',').map(s => s.trim()).filter(s => /^\d+$/.test(s))
      if (layoutNums.length !== 3) {
        setToffoliIbmError(`INITIAL_LAYOUT must have exactly 3 entries (got ${layoutNums.length})`)
        setIsSubmittingToffoliIbm(false)
        return
      }
      if (toffoliBackend === 'fake_4x4') {
        setToffoliIbmError('Select a real IBM backend for IBM submission (not Fake 4x4)')
        setIsSubmittingToffoliIbm(false)
        return
      }
      const result = await api.submit({
        token: token,
        homework_id: homeworkId,
        code: codeToRun,
        backend: toffoliBackend,
        shots: toffoliShots,
        eval_method: 'toffoli_ibm',
      })
      setToffoliIbmResult(result)
    } catch (err: any) {
      setToffoliIbmError(err.detail || err.message || 'IBM submission failed')
    } finally {
      setIsSubmittingToffoliIbm(false)
    }
  }

  const handleClearToken = () => {
    if (homeworkId) {
      localStorage.removeItem(`hw_token_${homeworkId}`)
    }
    setHomeworkInfo(null)
    setToken('')
    setSubmissions([])
  }

  const handleSaveProfile = async () => {
    if (!token) return
    setProfileSaving(true)
    setProfileSaved(false)
    try {
      await api.updateProfile({
        token,
        display_name: displayName,
        method_name: methodName,
      })
      setProfileSaved(true)
      setTimeout(() => setProfileSaved(false), 2000)
    } catch { /* ignore */ }
    finally { setProfileSaving(false) }
  }

  // ============ Sidebar resize handlers ============

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    isResizing.current = true
    const startX = e.clientX
    const startWidth = sidebarWidth

    const onMouseMove = (e: MouseEvent) => {
      if (!isResizing.current) return
      const newWidth = Math.min(500, Math.max(200, startWidth + (e.clientX - startX)))
      setSidebarWidth(newWidth)
    }

    const onMouseUp = () => {
      isResizing.current = false
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }, [sidebarWidth])

  // ============ Circuit composer handlers ============

  const findNextColumn = useCallback((qubits: number[]): number => {
    let maxColumn = -1
    circuit.gates.forEach(gate => {
      if (gate.qubits.some(q => qubits.includes(q))) {
        maxColumn = Math.max(maxColumn, gate.column)
      }
    })
    return maxColumn + 1
  }, [circuit.gates])

  const addGate = useCallback((
    gateType: GateType, qubits: number[], column: number,
    params?: number[], classicalBit?: number, condition?: GateCondition
  ) => {
    const newGate: PlacedGate = {
      id: generateGateId(), type: gateType, qubits, column,
      params, classicalBit, condition,
    }
    setCircuit(prev => ({ ...prev, gates: [...prev.gates, newGate] }))
  }, [])

  const handleGateDragStart = useCallback((gateType: GateType) => {
    setDraggingGate(gateType)
  }, [])

  const handleDropGate = useCallback((gateType: GateType, qubitIndex: number, column: number) => {
    const gate = GATE_DEFINITIONS[gateType]
    setDraggingGate(null)
    if (gateType === 'IF_ELSE') {
      setConditionalModal({ isOpen: true, qubitIndex, column })
      return
    }
    if (gateType === 'BARRIER') {
      const allQubits = Array.from({ length: circuit.numQubits }, (_, i) => i)
      addGate(gateType, allQubits, column)
      return
    }
    if (gate.numQubits === 1) {
      if (gate.numParams > 0) {
        setParamModal({ isOpen: true, gateType, qubitIndex, column })
      } else {
        const classicalBit = gateType === 'MEASURE' ? qubitIndex : undefined
        addGate(gateType, [qubitIndex], column, undefined, classicalBit)
      }
    } else {
      if (gate.numParams > 0) {
        setParamModal({ isOpen: true, gateType, qubitIndex, column })
      } else {
        setQubitModal({ isOpen: true, gateType, column })
      }
    }
  }, [addGate, circuit.numQubits])

  const handleParamConfirm = useCallback((params: number[]) => {
    const { gateType, qubitIndex, column } = paramModal
    if (!gateType) return
    const gate = GATE_DEFINITIONS[gateType]
    if (gate.numQubits === 1) {
      addGate(gateType, [qubitIndex], column, params)
    } else {
      setQubitModal({ isOpen: true, gateType, column, pendingParams: params })
    }
    setParamModal({ isOpen: false, gateType: null, qubitIndex: 0, column: 0 })
  }, [paramModal, addGate])

  const handleQubitConfirm = useCallback((qubits: number[]) => {
    const { gateType, pendingParams } = qubitModal
    if (!gateType) return
    const actualColumn = findNextColumn(qubits)
    addGate(gateType, qubits, actualColumn, pendingParams)
    setQubitModal({ isOpen: false, gateType: null, column: 0 })
  }, [qubitModal, addGate, findNextColumn])

  const handleRemoveGate = useCallback((gateId: string) => {
    setCircuit(prev => ({ ...prev, gates: prev.gates.filter(g => g.id !== gateId) }))
  }, [])

  const handleMoveGate = useCallback((gateId: string, newQubitIndex: number, newColumn: number) => {
    setCircuit(prev => {
      const gateToMove = prev.gates.find(g => g.id === gateId)
      if (!gateToMove) return prev
      if (gateToMove.qubits.length === 1) {
        return {
          ...prev,
          gates: prev.gates.map(g =>
            g.id === gateId
              ? { ...g, qubits: [newQubitIndex], column: newColumn, classicalBit: g.type === 'MEASURE' ? newQubitIndex : g.classicalBit }
              : g
          ),
        }
      }
      return { ...prev, gates: prev.gates.map(g => g.id === gateId ? { ...g, column: newColumn } : g) }
    })
  }, [])

  const handleConditionalConfirm = useCallback((gateType: GateType, condition: GateCondition) => {
    const { qubitIndex, column } = conditionalModal
    const gate = GATE_DEFINITIONS[gateType]
    if (gate.numParams > 0) {
      addGate(gateType, [qubitIndex], column, [Math.PI / 2], undefined, condition)
    } else {
      addGate(gateType, [qubitIndex], column, undefined, undefined, condition)
    }
    setConditionalModal({ isOpen: false, qubitIndex: 0, column: 0 })
  }, [conditionalModal, addGate])

  const addQubit = () => {
    if (circuit.numQubits >= 10) return
    setCircuit(prev => ({ ...prev, numQubits: prev.numQubits + 1, numClassicalBits: prev.numClassicalBits + 1 }))
  }

  const removeQubit = () => {
    if (circuit.numQubits <= 1) return
    const n = circuit.numQubits - 1
    setCircuit(prev => ({
      ...prev, numQubits: n, numClassicalBits: n,
      gates: prev.gates.filter(g => g.qubits.every(q => q < n)),
    }))
  }

  // ============ Mode switching ============

  // Track whether the circuit was compiled from Qrisp (read-only until edited)
  const [composerFromQrisp, setComposerFromQrisp] = useState(false)
  const [composerModified, setComposerModified] = useState(false)
  const circuitChangeCount = useRef(0)
  const circuitBaseCount = useRef(0)

  // Detect circuit modifications when viewing Qrisp-compiled circuit
  useEffect(() => {
    circuitChangeCount.current += 1
    if (composerFromQrisp && editorMode === 'composer' && circuitChangeCount.current > circuitBaseCount.current) {
      setComposerModified(true)
      setParseWarning('You modified the circuit. Language will switch to Qiskit when you return to Code Editor.')
    }
  }, [circuit]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSwitchToCode = () => {
    if (editorMode === 'composer') {
      const compiled = isToffoli
        ? compileToQiskit(circuit, undefined, undefined, { noClassicalBits: true })
        : compileToQiskit(circuit, postSelect, initialLayout || undefined)
      if (compiled && compiled.trim()) {
        setCode(compiled)
      }
      // If circuit was from Qrisp and user modified it, force-switch to Qiskit
      if (composerFromQrisp && composerModified) {
        setSelectedLanguage('qiskit')
        setParseWarning('Circuit was modified in the composer. Language switched to Qiskit (cannot convert back to Qrisp).')
      } else if (composerFromQrisp && !composerModified) {
        // User didn't modify — restore Qrisp code
        const savedQrisp = localStorage.getItem(`challenge_draft_${homeworkId}_qrisp`)
        if (savedQrisp) {
          setCode(savedQrisp)
        }
        setParseWarning(null)
      } else {
        setParseWarning(null)
      }
      setComposerFromQrisp(false)
      setComposerModified(false)
    }
    setEditorMode('code')
  }

  const handleSwitchToComposer = async () => {
    if (editorMode === 'code') {
      let codeToDecompile = code

      // If language is Qrisp, compile to Qiskit first via backend
      if (challengeMode && selectedLanguage === 'qrisp') {
        setParseWarning('Compiling Qrisp to circuit...')
        try {
          const result = await challengeApi.compileQrisp(code)
          if (result.success && result.qiskit_code) {
            codeToDecompile = result.qiskit_code
            setComposerFromQrisp(true)
            setComposerModified(false)
            // Mark base count so we can detect future changes
            circuitBaseCount.current = circuitChangeCount.current + 1
            // Save Qrisp code so we can restore it later
            localStorage.setItem(`challenge_draft_${homeworkId}_qrisp`, code)
          } else {
            setParseWarning(`Qrisp compilation failed: ${result.error || 'Unknown error'}. Cannot show circuit.`)
            return
          }
        } catch (err: any) {
          setParseWarning(`Qrisp compilation failed: ${err?.message || 'Network error'}`)
          return
        }
      } else {
        setComposerFromQrisp(false)
        setComposerModified(false)
      }

      const result = decompileToCircuit(codeToDecompile)
      if (result) {
        setCircuit(result.circuit)
        if (result.postSelect) {
          setPostSelect(result.postSelect)
        }
        setInitialLayout(result.initialLayout || '')
        if (composerFromQrisp) {
          setParseWarning('Showing compiled Qrisp circuit. Editing will switch your language to Qiskit.')
        } else {
          setParseWarning(null)
        }
      } else {
        setParseWarning('Could not parse code into visual circuit. The composer shows the previous circuit.')
      }
    }
    setEditorMode('composer')
  }

  // Budget percentage (only meaningful when token is verified)
  const budgetPercent = homeworkInfo?.valid
    ? Math.min(100, ((homeworkInfo.budget_used_seconds || 0) / (homeworkInfo.budget_total_seconds || 1)) * 100)
    : 0
  const budgetLow = (homeworkInfo?.budget_remaining_seconds || 0) < 60

  const isTokenVerified = homeworkInfo?.valid === true
  const canSubmitHardware = isTokenVerified && (homeworkInfo?.budget_remaining_seconds || 0) >= 30

  // ============ Preview compiled test circuit ============

  const generatePreviewCode = (basis?: string): string => {
    const studentCode = editorMode === 'composer' ? compileToQiskit(circuit, postSelect, initialLayout || undefined) : code
    // Strip measurements and POST_SELECT from student code
    const lines = studentCode.split('\n').filter(l => {
      const t = l.trim()
      if (t.startsWith('POST_SELECT')) return false
      if (/\.measure_all\s*\(/.test(t)) return false
      if (/\.measure\s*\(/.test(t)) return false
      if (t.startsWith('# Post-selection')) return false
      if (t.startsWith('# Keep shots')) return false
      if (t.startsWith('# For BBPSSW')) return false
      return true
    })

    const header = `# ===== FULL TEST CIRCUIT =====\n# This is what actually runs on hardware.\n# Your circuit + ${evalMethod === 'tomography' ? `Tomography (${basis} basis)` : 'Inverse Bell verification'}\n`

    if (evalMethod === 'inverse_bell') {
      return header + lines.join('\n') + `

# --- Inverse Bell Verification ---
# Maps |Phi+> -> |00>: F(Phi+) = P(output qubits = 00)
qc.cx(0, 1)
qc.h(0)
qc.measure_all()
`
    } else {
      // Tomography
      const b = basis || 'ZZ'
      let basisGates = ''
      if (b === 'XX') {
        basisGates = `# XX basis: apply H on output qubits before measurement
qc.h(0)
qc.h(1)`
      } else if (b === 'YY') {
        basisGates = `# YY basis: apply Sdg+H on output qubits before measurement
qc.sdg(0)
qc.h(0)
qc.sdg(1)
qc.h(1)`
      } else {
        basisGates = `# ZZ basis: measure in computational basis (no extra gates)`
      }
      return header + lines.join('\n') + `

# --- Tomography (${b} basis) ---
${basisGates}
qc.measure_all()
`
    }
  }

  // ============ Loading / Error States ============

  if (loadError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-qcloud-light via-white to-qcloud-bg flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-red-500 mb-4">{loadError}</p>
          <Link to="/" className="text-qcloud-primary hover:underline">Back to Home</Link>
        </div>
      </div>
    )
  }

  if (!publicInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-qcloud-light via-white to-qcloud-bg flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-qcloud-primary border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  // ============ Main Editor Layout ============

  return (
    <div className="h-screen flex flex-col bg-white">
      {/* Header */}
      <header className="h-12 flex-shrink-0 flex items-center justify-between px-4 border-b border-qcloud-border bg-white">
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80">
            <Logo size="small" />
          </Link>
          <div className="h-6 w-px bg-qcloud-border" />
          <span className="text-sm font-semibold text-qcloud-text">
            {publicInfo.title}
          </span>
          <span className="text-xs text-qcloud-muted">
            {publicInfo.course}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {/* Budget bar (only when token verified) */}
          {isTokenVerified && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-qcloud-muted">Budget:</span>
              <div className="w-24 bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${budgetLow ? 'bg-red-500' : 'bg-qcloud-primary'}`}
                  style={{ width: `${budgetPercent}%` }}
                />
              </div>
              <span className={`text-xs font-medium ${budgetLow ? 'text-red-500' : 'text-qcloud-muted'}`}>
                {Math.round(homeworkInfo?.budget_remaining_seconds || 0)}s
              </span>
            </div>
          )}
          {homeworkId && (
            <>
              <Link
                to={`${baseUrl}/leaderboard${token ? `?token=${encodeURIComponent(token)}` : ''}`}
                className="px-3 py-1 text-xs bg-amber-50 text-amber-700 rounded hover:bg-amber-100 transition-colors"
              >
                Leaderboard
              </Link>
              <Link
                to={`${baseUrl}/queue${token ? `?token=${encodeURIComponent(token)}` : ''}`}
                className="px-3 py-1 text-xs bg-green-50 text-green-700 rounded hover:bg-green-100 transition-colors"
              >
                Queue
              </Link>
              <Link
                to={`${baseUrl}/hardware-ranking`}
                className="px-3 py-1 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100 transition-colors"
              >
                Hardware Ranking
              </Link>
              {isTokenVerified && (
                <Link
                  to={`${baseUrl}/job-history?token=${encodeURIComponent(token)}`}
                  className="px-3 py-1 text-xs bg-purple-50 text-purple-700 rounded hover:bg-purple-100 transition-colors"
                >
                  Job History
                </Link>
              )}
            </>
          )}
          {isAdmin && (
            challengeMode ? (
              <Link
                to={`/admin/challenges/${homeworkId}`}
                className="px-3 py-1 text-xs rounded font-medium transition-colors bg-purple-50 text-purple-700 hover:bg-purple-100"
              >
                Admin Panel
              </Link>
            ) : (
              <button
                onClick={() => setShowAdmin(!showAdmin)}
                className={`px-3 py-1 text-xs rounded font-medium transition-colors ${
                  showAdmin
                    ? 'bg-red-500 text-white hover:bg-red-600'
                    : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
                }`}
              >
                {showAdmin ? 'Student View' : 'Admin Panel'}
              </button>
            )
          )}
        </div>
      </header>

      {/* Admin Panel (replaces editor when active) */}
      {showAdmin && homeworkId ? (
        <HomeworkAdminPanel homeworkId={homeworkId} />
      ) : (
      <>
      {/* Tab Bar */}
      <div className="h-9 flex-shrink-0 bg-slate-800 px-4 flex items-center justify-between">
        <div className="flex items-center gap-1">
          <button
            onClick={handleSwitchToCode}
            className={`px-3 py-1.5 rounded-t text-xs flex items-center gap-1.5 transition-colors ${
              editorMode === 'code'
                ? 'bg-slate-900 text-slate-200'
                : 'text-slate-400 hover:text-slate-300'
            }`}
          >
            <span className="font-mono text-[10px]">{'</>'}</span> Code Editor
          </button>
          <button
            onClick={handleSwitchToComposer}
            className={`px-3 py-1.5 rounded-t text-xs flex items-center gap-1.5 transition-colors ${
              editorMode === 'composer'
                ? 'bg-slate-900 text-slate-200'
                : 'text-slate-400 hover:text-slate-300'
            }`}
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            Circuit Composer
          </button>
          <div className="w-px h-4 bg-slate-600 mx-1" />
          <span className={`text-[10px] ${codeSaved ? 'text-green-400' : 'text-yellow-400'}`}>
            {codeSaved ? 'Draft saved' : 'Saving...'}
          </span>
          <button
            onClick={() => {
              if (window.confirm('Reset editor to starter code? Your current draft will be lost.')) {
                setCode(challengeMode && publicInfo?.starter_code ? publicInfo.starter_code : isToffoli ? TOFFOLI_STARTER_CODE : STARTER_CODE)
                localStorage.removeItem(`hw_draft_code_${homeworkId}`)
              }
            }}
            className="px-2 py-0.5 text-[10px] text-red-400 border border-red-400/30 rounded hover:bg-red-400/10 transition-colors"
            title="Reset to starter code (clears your draft)"
          >
            Reset
          </button>
        </div>
        <div className="flex items-center gap-2">
          {isToffoli ? (
            <>
              {/* Toffoli mode: Test + Layout + Transpile + HS Submit + Hardware Submit */}
              <button
                onClick={handleToffoliSimulate}
                disabled={isSimulatingToffoli}
                className="px-4 py-1 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-500 transition-colors disabled:opacity-50"
              >
                {isSimulatingToffoli ? 'Evaluating...' : 'Test Circuit'}
              </button>
              <div className="w-px h-4 bg-slate-600" />
              <span className="text-[10px] text-slate-400">Backend:</span>
              <select
                value={toffoliBackend}
                onChange={(e) => setToffoliBackend(e.target.value)}
                className="px-2 py-1 bg-slate-700 text-slate-200 border border-slate-600 rounded text-xs focus:outline-none"
              >
                <option value="hs_calculator">HS Calculator</option>
                <option value="fake_4x4">Fake 4x4 Grid</option>
                {HARDWARE_PROVIDERS.filter(p => p.type !== 'simulator').map((p) => (
                  <option key={p.backendName} value={p.backendName}>{p.name}</option>
                ))}
              </select>
              <span className="text-[10px] text-slate-400">Shots:</span>
              <input
                type="number"
                value={toffoliShots}
                onChange={(e) => setToffoliShots(Math.max(1, Math.min(8192, parseInt(e.target.value) || 1024)))}
                className="w-16 px-2 py-1 bg-slate-700 text-slate-200 border border-slate-600 rounded text-xs focus:outline-none"
              />
              <div className="w-px h-4 bg-slate-600" />
              {/* Layout button */}
              {toffoliBackend && getCalibrationData(toffoliBackend) && (
                <button
                  onClick={() => {
                    if (editorMode === 'code') {
                      const match = code.match(/^[^#\n]*INITIAL_LAYOUT\s*=\s*\[([^\]]*)\]/m)
                      if (match) setInitialLayout(match[1].trim())
                    }
                    setShowHardwareInfo(true)
                  }}
                  className="px-2 py-1 bg-slate-700 text-blue-300 border border-slate-600 rounded text-xs hover:bg-slate-600 hover:text-blue-100 transition-colors"
                  title="Preview the hardware topology and your qubit layout"
                >
                  Layout
                </button>
              )}
              {/* Transpile button */}
              <button
                onClick={async () => {
                  setIsCheckingToffoliTranspile(true)
                  setToffoliTranspileResult(null)
                  setToffoliTranspileInput('00')
                  setShowToffoliTranspile(true)
                  try {
                    const codeToCheck = editorMode === 'composer' ? compileToQiskit(circuit, undefined, undefined, { noClassicalBits: true }) : code
                    const result = await homeworkApi.checkToffoliTranspile({
                      homework_id: homeworkId!,
                      code: codeToCheck,
                      backend_name: toffoliBackend,
                    })
                    setToffoliTranspileResult(result)
                  } catch (err: any) {
                    setToffoliTranspileResult({ success: false, error: err.message || 'Failed to check transpilation' })
                  } finally {
                    setIsCheckingToffoliTranspile(false)
                  }
                }}
                disabled={isCheckingToffoliTranspile}
                className="px-2 py-1 bg-slate-700 text-green-300 border border-slate-600 rounded text-xs hover:bg-slate-600 hover:text-green-100 transition-colors disabled:opacity-50"
                title="Preview transpiled circuits for all 4 input cases"
              >
                {isCheckingToffoliTranspile ? 'Transpiling...' : 'Transpile'}
              </button>
              <div className="w-px h-4 bg-slate-600" />
              <button
                onClick={handleToffoliSubmit}
                disabled={isSubmittingToffoli || (!isTokenVerified && !isAdmin) || toffoliBackend !== 'hs_calculator'}
                title={!isTokenVerified && !isAdmin ? 'Enter your token first' : toffoliBackend !== 'hs_calculator' ? 'Select HS Calculator backend first' : 'Compute Hilbert-Schmidt distance'}
                className="px-4 py-1 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-500 transition-colors disabled:opacity-50"
              >
                {isSubmittingToffoli ? 'Submitting...' : 'Submit HS'}
              </button>
              <button
                onClick={handleToffoliHardwareSubmit}
                disabled={isSubmittingToffoliHw || (!isTokenVerified && !isAdmin) || toffoliBackend !== 'fake_4x4'}
                title={!isTokenVerified && !isAdmin ? 'Enter your token first' : toffoliBackend !== 'fake_4x4' ? 'Select Fake 4x4 Grid backend first' : 'Submit circuit to fake hardware for execution'}
                className="px-4 py-1 bg-orange-600 text-white rounded text-xs font-medium hover:bg-orange-500 transition-colors disabled:opacity-50"
              >
                {isSubmittingToffoliHw ? 'Submitting...' : 'Submit Fake HW'}
              </button>
              <button
                onClick={handleToffoliIbmSubmit}
                disabled={isSubmittingToffoliIbm || (!isTokenVerified && !isAdmin) || toffoliBackend === 'fake_4x4' || toffoliBackend === 'hs_calculator'}
                title={!isTokenVerified && !isAdmin ? 'Enter your token first' : (toffoliBackend === 'fake_4x4' || toffoliBackend === 'hs_calculator') ? 'Select a real IBM backend first' : 'Runs all 4 input combinations (000,010,100,110) on real IBM hardware and computes average fidelity'}
                className="px-4 py-1 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-500 transition-colors disabled:opacity-50"
              >
                {isSubmittingToffoliIbm ? 'Submitting...' : 'Submit to IBM'}
              </button>
              <span className="text-[9px] text-slate-400 hidden md:inline">All 4 inputs, avg fidelity</span>
            </>
          ) : (
            <>
          <span className="text-[10px] text-slate-400">Backend:</span>
          <select
            value={selectedBackend}
            onChange={(e) => setSelectedBackend(e.target.value)}
            className="px-2 py-1 bg-slate-700 text-slate-200 border border-slate-600 rounded text-xs focus:outline-none"
          >
            {HARDWARE_PROVIDERS.map((p) => (
              <option key={p.backendName} value={p.backendName}>{p.name}</option>
            ))}
          </select>
          {!challengeMode && (
          <button
            onClick={() => setShowHardwareInfo(true)}
            className="px-1.5 py-1 bg-slate-700 text-slate-300 border border-slate-600 rounded text-xs hover:bg-slate-600 hover:text-white transition-colors"
            title="View hardware details"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
          )}
          <div className="w-px h-4 bg-slate-600" />
          <span className="text-[10px] text-slate-400">Shots:</span>
          <input
            type="number"
            value={shots}
            onChange={(e) => setShots(Math.max(1, Math.min(8192, parseInt(e.target.value) || 1024)))}
            className="w-16 px-2 py-1 bg-slate-700 text-slate-200 border border-slate-600 rounded text-xs focus:outline-none"
          />
          {/* Test Method + Preview Circuit: only for homework (challenges use test_code) */}
          {!challengeMode && (
          <>
          <div className="w-px h-4 bg-slate-600" />
          <span className="text-[10px] text-slate-400">Test Method:</span>
          <select
            value={evalMethod}
            onChange={(e) => setEvalMethod(e.target.value as 'inverse_bell' | 'tomography')}
            className="px-2 py-1 bg-slate-700 text-slate-200 border border-slate-600 rounded text-xs focus:outline-none"
            title="Fidelity evaluation method"
          >
            <option value="inverse_bell">Inverse Bell</option>
            <option value="tomography">Tomography</option>
          </select>
          <button
            onClick={() => setShowPreviewCircuit(true)}
            className="px-2 py-1 bg-slate-700 text-slate-300 border border-slate-600 rounded text-xs hover:bg-slate-600 hover:text-white transition-colors"
            title="Preview the full compiled circuit including the test method"
          >
            Preview Circuit
          </button>
          </>
          )}
          {selectedBackend && getCalibrationData(selectedBackend) && (
            <button
              onClick={() => {
                // Extract INITIAL_LAYOUT from code before opening modal
                if (editorMode === 'code') {
                  const match = code.match(/^[^#\n]*INITIAL_LAYOUT\s*=\s*\[([^\]]*)\]/m)
                  if (match) {
                    setInitialLayout(match[1].trim())
                  }
                }
                setShowHardwareInfo(true)
              }}
              className="px-2 py-1 bg-slate-700 text-blue-300 border border-slate-600 rounded text-xs hover:bg-slate-600 hover:text-blue-100 transition-colors"
              title="Preview the hardware topology and your qubit layout"
            >
              Layout
            </button>
          )}
          {selectedBackend && (
            <button
              onClick={async () => {
                setIsCheckingTranspile(true)
                setTranspileResult(null)
                setShowTranspileCheck(true)
                try {
                  const codeToCheck = editorMode === 'composer' ? compileToQiskit(circuit, postSelect, initialLayout || undefined) : code
                  const result = await api.checkTranspile({
                    homework_id: homeworkId!,
                    code: codeToCheck,
                    backend_name: selectedBackend,
                    eval_method: evalMethod,
                  })
                  setTranspileResult(result)
                } catch (err: any) {
                  setTranspileResult({ success: false, error: err.message || 'Failed to check transpilation' })
                } finally {
                  setIsCheckingTranspile(false)
                }
              }}
              disabled={isCheckingTranspile}
              className="px-2 py-1 bg-slate-700 text-green-300 border border-slate-600 rounded text-xs hover:bg-slate-600 hover:text-green-100 transition-colors disabled:opacity-50"
              title="Check how your code will be transpiled for the selected backend"
            >
              {isCheckingTranspile ? 'Checking...' : 'Transpile'}
            </button>
          )}
          <Link
            to={`${baseUrl}/methods`}
            className="px-2 py-1 bg-slate-700 text-slate-300 border border-slate-600 rounded text-xs hover:bg-slate-600 hover:text-white transition-colors"
            title="Learn how fidelity is calculated"
          >
            ?
          </Link>
          <div className="w-px h-4 bg-slate-600" />
          {/* Noise model error rates */}
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-slate-400">1Q err:</span>
            <input
              type="number"
              value={singleQubitError}
              onChange={e => setSingleQubitError(Math.max(0, Math.min(0.5, parseFloat(e.target.value) || 0)))}
              step={0.005}
              min={0}
              max={0.5}
              className="w-14 px-1 py-0.5 bg-slate-700 border border-slate-600 rounded text-[10px] text-slate-200 text-center"
            />
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-slate-400">2Q err:</span>
            <input
              type="number"
              value={twoQubitError}
              onChange={e => setTwoQubitError(Math.max(0, Math.min(0.5, parseFloat(e.target.value) || 0)))}
              step={0.005}
              min={0}
              max={0.5}
              className="w-14 px-1 py-0.5 bg-slate-700 border border-slate-600 rounded text-[10px] text-slate-200 text-center"
            />
          </div>
          {/* Simulator button - only for noisy_simulator backend, requires token (admins bypass token) */}
          <button
            onClick={() => handleSimulate('distillation')}
            disabled={isSimulating || selectedBackend !== 'noisy_simulator' || (!isTokenVerified && !isAdmin)}
            title={!isTokenVerified && !isAdmin ? 'Enter your token first' : selectedBackend !== 'noisy_simulator' ? 'Select Noisy Simulator backend' : undefined}
            className="px-4 py-1 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-500 transition-colors disabled:opacity-50"
          >
            {isSimulating && simulateMode === 'distillation' ? 'Simulating...' : 'Run Simulator'}
          </button>
          {/* Hardware submit - only for real IBM backends, requires token + budget (admins bypass token) */}
          <button
            onClick={openSubmitModal}
            disabled={isSubmitting || selectedBackend === 'noisy_simulator' || selectedBackend === 'fake_4x4' || (!canSubmitHardware && !isAdmin)}
            title={!isTokenVerified && !isAdmin ? 'Enter your token first' : selectedBackend === 'noisy_simulator' || selectedBackend === 'fake_4x4' ? 'Select a real hardware backend' : !canSubmitHardware && !isAdmin ? 'Insufficient budget' : undefined}
            className="px-4 py-1 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-500 transition-colors disabled:opacity-50"
          >
            {isSubmitting ? 'Submitting...' : 'Submit to Hardware'}
          </button>
          {/* Fake hardware submit - only for fake_4x4 backend, requires token (admins bypass token) */}
          <button
            onClick={handleSubmitFakeHardware}
            disabled={isSubmittingFakeHw || selectedBackend !== 'fake_4x4' || (!isTokenVerified && !isAdmin)}
            title={!isTokenVerified && !isAdmin ? 'Enter your token first' : selectedBackend !== 'fake_4x4' ? 'Select Fake 4x4 Grid backend' : undefined}
            className="px-4 py-1 bg-orange-600 text-white rounded text-xs font-medium hover:bg-orange-500 transition-colors disabled:opacity-50"
          >
            {isSubmittingFakeHw ? 'Submitting...' : 'Submit to FakeHardware'}
          </button>
            </>
          )}
        </div>
      </div>

      {/* Toffoli Results Panel */}
      {isToffoli && (toffoliResult || toffoliSubmitResult || toffoliError) && (
        <div className="border-b border-qcloud-border bg-slate-50">
          <div className="px-4 py-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-qcloud-text uppercase tracking-wider">
              {toffoliSubmitResult ? 'Submission Result' : 'Test Result'}
            </span>
            <button
              onClick={() => { setToffoliResult(null); setToffoliSubmitResult(null); setToffoliError(null) }}
              className="text-xs text-qcloud-muted hover:text-red-500"
            >
              Clear
            </button>
          </div>
          {toffoliError ? (
            <div className="px-4 pb-3 text-xs text-red-600">{toffoliError}</div>
          ) : (toffoliResult?.success || toffoliSubmitResult?.success) ? (
            <div className="px-4 pb-3">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className={`text-xl font-bold ${
                    (toffoliSubmitResult?.hs_distance ?? toffoliResult?.hs_distance ?? 99) < 0.5 ? 'text-green-600' :
                    (toffoliSubmitResult?.hs_distance ?? toffoliResult?.hs_distance ?? 99) < 1 ? 'text-amber-600' :
                    'text-red-600'
                  }`}>
                    {(toffoliSubmitResult?.hs_distance ?? toffoliResult?.hs_distance)?.toFixed(4)}
                  </div>
                  <div className="text-[10px] text-qcloud-muted">HS Distance</div>
                </div>
                <div>
                  <div className="text-xl font-bold text-purple-600">
                    {toffoliSubmitResult?.score ?? toffoliResult?.score ?? 0}
                  </div>
                  <div className="text-[10px] text-qcloud-muted">Score</div>
                </div>
                <div>
                  <div className="text-sm font-mono text-qcloud-muted">
                    {toffoliSubmitResult?.gate_count ?? toffoliResult?.gate_count}G / D{toffoliSubmitResult?.circuit_depth ?? toffoliResult?.circuit_depth}
                  </div>
                  <div className="text-[10px] text-qcloud-muted">Gates / Depth</div>
                </div>
              </div>
              {toffoliSubmitResult?.submission_id && (
                <div className="mt-2 text-xs text-green-600 text-center">
                  Submitted successfully! Check the leaderboard.
                </div>
              )}
              {toffoliResult && !toffoliSubmitResult && (
                <div className="mt-2 text-xs text-qcloud-muted text-center">
                  This is a test result. Click "Submit for Leaderboard" to save it.
                </div>
              )}
            </div>
          ) : (
            <div className="px-4 pb-3 text-xs text-red-600">
              {(toffoliResult as any)?.error || (toffoliSubmitResult as any)?.error || 'Evaluation failed'}
            </div>
          )}
        </div>
      )}

      {/* Toffoli Hardware Results Panel */}
      {isToffoli && (toffoliHwResult || toffoliHwError) && (
        <div className="border-b border-qcloud-border bg-orange-50">
          <div className="px-4 py-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-orange-700 uppercase tracking-wider">
              Hardware Result
            </span>
            <button
              onClick={() => { setToffoliHwResult(null); setToffoliHwError(null) }}
              className="text-xs text-qcloud-muted hover:text-red-500"
            >
              Clear
            </button>
          </div>
          {toffoliHwError ? (
            <div className="px-4 pb-3 text-xs text-red-600 whitespace-pre-line">{toffoliHwError}</div>
          ) : toffoliHwResult?.success ? (
            <div className="px-4 pb-3">
              <div className="grid grid-cols-3 gap-4 text-center mb-3">
                <div>
                  <div className={`text-xl font-bold ${
                    (toffoliHwResult.fidelity_after ?? 0) >= 0.9 ? 'text-green-600' :
                    (toffoliHwResult.fidelity_after ?? 0) >= 0.7 ? 'text-amber-600' :
                    'text-red-600'
                  }`}>
                    {((toffoliHwResult.fidelity_after ?? 0) * 100).toFixed(2)}%
                  </div>
                  <div className="text-[10px] text-qcloud-muted">Average Fidelity</div>
                </div>
                <div>
                  <div className="text-sm font-mono text-qcloud-muted">
                    {toffoliHwResult.gate_count}G / D{toffoliHwResult.circuit_depth}
                  </div>
                  <div className="text-[10px] text-qcloud-muted">Gates / Depth</div>
                </div>
                <div>
                  <div className="text-sm font-mono text-qcloud-muted">
                    {toffoliHwResult.initial_layout ? `[${toffoliHwResult.initial_layout.join(', ')}]` : '--'}
                  </div>
                  <div className="text-[10px] text-qcloud-muted">Layout</div>
                </div>
              </div>
              {toffoliHwResult.per_input_fidelities && (
                <div className="flex items-center justify-center gap-4 text-xs">
                  <span className="text-qcloud-muted font-medium">Per-Input:</span>
                  {Object.entries(toffoliHwResult.per_input_fidelities).map(([input, fid]) => (
                    <span key={input} className={`px-2 py-0.5 rounded font-mono ${
                      fid >= 0.9 ? 'bg-green-50 text-green-700' :
                      fid >= 0.7 ? 'bg-amber-50 text-amber-700' :
                      'bg-red-50 text-red-700'
                    }`}>
                      F({input}): {(fid * 100).toFixed(1)}%
                    </span>
                  ))}
                </div>
              )}
              {toffoliHwResult.submission_id && (
                <div className="mt-2 text-xs text-green-600 text-center">
                  Submitted successfully! Check the hardware leaderboard.
                </div>
              )}
            </div>
          ) : (
            <div className="px-4 pb-3 text-xs text-red-600">
              {toffoliHwResult?.error || 'Hardware submission failed'}
            </div>
          )}
        </div>
      )}

      {/* Toffoli IBM Results Panel */}
      {isToffoli && (toffoliIbmResult || toffoliIbmError) && (
        <div className="border-b border-qcloud-border bg-blue-50">
          <div className="px-4 py-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-blue-700 uppercase tracking-wider">
              IBM Hardware Result
            </span>
            <button
              onClick={() => { setToffoliIbmResult(null); setToffoliIbmError(null) }}
              className="text-xs text-qcloud-muted hover:text-red-500"
            >
              Clear
            </button>
          </div>
          {toffoliIbmError ? (
            <div className="px-4 pb-3 text-xs text-red-600 whitespace-pre-line">{toffoliIbmError}</div>
          ) : toffoliIbmResult ? (
            <div className="px-4 pb-3">
              <div className="text-xs text-blue-700">
                Job queued successfully!
                {toffoliIbmResult.submission_id && (
                  <span className="ml-2 font-mono">ID: {toffoliIbmResult.submission_id}</span>
                )}
                {toffoliIbmResult.queue_position != null && (
                  <span className="ml-2">Queue position: {toffoliIbmResult.queue_position}</span>
                )}
              </div>
              <div className="mt-1 text-xs text-blue-600">
                Evaluation: all 4 input combinations (|000&#x27E9;, |010&#x27E9;, |100&#x27E9;, |110&#x27E9;) are tested and average fidelity is computed. Check the IBM Hardware leaderboard for results once the job completes.
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* Submit error */}
      {submitError && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-200 text-xs text-red-700 flex items-center justify-between">
          <span>{submitError}</span>
          <button onClick={() => setSubmitError(null)} className="text-red-500 hover:text-red-700 ml-2">x</button>
        </div>
      )}

      {/* Simulator Results Panel */}
      {(simulatorResult || simulateError) && (
        <div className="border-b border-qcloud-border bg-slate-50">
          <div className="px-4 py-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-qcloud-text uppercase tracking-wider">
              {simulateMode === 'bell_pair' ? 'Bell Pair Test Results' : 'Simulator Results (Noisy)'}
            </span>
            <button
              onClick={() => { setSimulatorResult(null); setSimulateError(null) }}
              className="text-xs text-qcloud-muted hover:text-red-500"
            >
              Clear
            </button>
          </div>
          {simulateError ? (
            <div className="px-4 pb-3 text-xs text-red-600">{simulateError}</div>
          ) : simulatorResult && simulatorResult.success ? (
            <div className="px-4 pb-3">
              {simulateMode === 'bell_pair' ? (
                /* Bell pair mode: just show fidelity */
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-[10px] text-qcloud-muted">Bell Pair Fidelity</div>
                    <div className="text-lg font-bold text-indigo-600">
                      {((simulatorResult.fidelity_after || 0) * 100).toFixed(1)}%
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] text-qcloud-muted">Ideal</div>
                    <div className="text-lg font-bold text-gray-400">100%</div>
                  </div>
                </div>
              ) : (
                /* Distillation mode: results */
                <div className={`grid ${simulatorResult.success_probability != null ? 'grid-cols-2' : 'grid-cols-1'} gap-4 text-center`}>
                  <div>
                    <div className="text-[10px] text-qcloud-muted">Your Fidelity</div>
                    <div className="text-lg font-bold text-qcloud-primary">
                      {((simulatorResult.fidelity_after || 0) * 100).toFixed(1)}%
                    </div>
                  </div>
                  {simulatorResult.success_probability != null && (
                    <div>
                      <div className="text-[10px] text-qcloud-muted">Success Prob.</div>
                      <div className="text-lg font-bold text-purple-600">
                        {(simulatorResult.success_probability * 100).toFixed(1)}%
                        {simulatorResult.post_selected_shots != null && (
                          <span className="text-[9px] font-normal text-qcloud-muted ml-1">
                            ({simulatorResult.post_selected_shots} shots)
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {simulatorResult.tomography_correlators && (
                <div className="mt-2 flex gap-3 text-xs">
                  <span className="text-qcloud-muted font-medium">Correlators:</span>
                  {Object.entries(simulatorResult.tomography_correlators).map(([basis, val]) => (
                    <span key={basis} className="font-mono">
                      <span className="text-purple-600">{basis}</span>={typeof val === 'number' ? val.toFixed(3) : val}
                    </span>
                  ))}
                  <span className="text-qcloud-muted">F = (1+XX-YY+ZZ)/4</span>
                </div>
              )}
              <div className="mt-2 text-[10px] text-qcloud-muted flex gap-4">
                <span>Qubits: {simulatorResult.qubit_count}</span>
                <span>Gates: {simulatorResult.gate_count}</span>
                <span>Depth: {simulatorResult.circuit_depth}</span>
                <span>Time: {simulatorResult.execution_time_ms?.toFixed(0)}ms</span>
                <span className={`ml-auto px-1.5 py-0.5 rounded ${
                  simulateMode === 'bell_pair' ? 'bg-indigo-100 text-indigo-700' :
                  simulatorResult.eval_method === 'tomography' ? 'bg-purple-100 text-purple-700' :
                  'bg-blue-100 text-blue-700'
                }`}>
                  {simulateMode === 'bell_pair' ? 'Bell Pair Test' :
                   simulatorResult.eval_method === 'tomography' ? 'Tomography' :
                   simulatorResult.eval_method === 'inverse_bell' ? 'Inverse Bell' : 'Noisy Simulator'}
                </span>
              </div>
            </div>
          ) : simulatorResult && !simulatorResult.success ? (
            <div className="px-4 pb-3 text-xs text-red-600">{simulatorResult.error}</div>
          ) : null}
        </div>
      )}

      {/* Fake Hardware Results Panel */}
      {(fakeHwResult || fakeHwError) && (
        <div className="border-b border-qcloud-border bg-orange-50">
          <div className="px-4 py-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-qcloud-text uppercase tracking-wider">
              Fake Hardware Results (4x4 Grid)
            </span>
            <button
              onClick={() => { setFakeHwResult(null); setFakeHwError(null) }}
              className="text-xs text-qcloud-muted hover:text-red-500"
            >
              Clear
            </button>
          </div>
          {fakeHwError ? (
            <div className="px-4 pb-3 text-xs text-red-600">{fakeHwError}</div>
          ) : fakeHwResult && fakeHwResult.success ? (
            <div className="px-4 pb-4 space-y-3">
              {/* Fidelity Banner */}
              <div className="bg-gradient-to-r from-orange-100 to-amber-100 rounded-lg p-3 text-center border border-orange-200">
                <div className="text-2xl font-bold text-orange-600">
                  Fidelity: {((fakeHwResult.fidelity_after || 0) * 100).toFixed(1)}%
                </div>
                <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                  fakeHwResult.eval_method === 'tomography' ? 'bg-purple-100 text-purple-700' :
                  'bg-teal-100 text-teal-700'
                }`}>
                  {fakeHwResult.eval_method === 'inverse_bell' ? 'Inverse Bell' : 'Tomography'}
                </span>
              </div>

              {/* Post-Selection Stats */}
              {fakeHwResult.success_probability != null && (
                <div className="grid grid-cols-2 gap-3 text-center">
                  <div className="bg-white rounded-lg p-2 border border-orange-200">
                    <div className="text-[10px] text-qcloud-muted">Success Probability</div>
                    <div className="text-sm font-bold text-amber-600">
                      {(fakeHwResult.success_probability * 100).toFixed(1)}%
                    </div>
                  </div>
                  {fakeHwResult.post_selected_shots != null && (
                    <div className="bg-white rounded-lg p-2 border border-orange-200">
                      <div className="text-[10px] text-qcloud-muted">Post-Selected Shots</div>
                      <div className="text-sm font-bold text-qcloud-text">
                        {fakeHwResult.post_selected_shots} / {shots}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Tomography Correlators */}
              {fakeHwResult.tomography_correlators && (
                <div className="bg-white rounded-lg p-2 border border-orange-200">
                  <div className="text-[10px] font-medium text-qcloud-text mb-1">Pauli Correlators</div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    {Object.entries(fakeHwResult.tomography_correlators).map(([basis, val]) => (
                      <div key={basis}>
                        <div className="text-[10px] text-qcloud-muted">{basis}</div>
                        <div className={`text-sm font-bold font-mono ${
                          typeof val === 'number' && val >= 0 ? 'text-green-600' : 'text-red-500'
                        }`}>
                          {typeof val === 'number' ? (val >= 0 ? '+' : '') + val.toFixed(3) : val}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[9px] text-qcloud-muted mt-1 text-center">
                    {'F(|Φ+⟩) = (1 + ⟨XX⟩ − ⟨YY⟩ + ⟨ZZ⟩) / 4'}
                  </p>
                </div>
              )}

              {/* Submission Details */}
              <div className="bg-white rounded-lg p-2 border border-orange-200">
                <div className="text-[10px] font-medium text-qcloud-text mb-1">Submission Details</div>
                <div className="grid grid-cols-4 gap-2 text-center text-[10px]">
                  <div>
                    <span className="text-qcloud-muted block">Backend</span>
                    <span className="font-medium text-orange-700">Fake 4x4</span>
                  </div>
                  <div>
                    <span className="text-qcloud-muted block">Qubits</span>
                    <span className="font-medium">{fakeHwResult.qubit_count ?? '—'}</span>
                  </div>
                  <div>
                    <span className="text-qcloud-muted block">Gates</span>
                    <span className="font-medium">{fakeHwResult.gate_count ?? '—'}</span>
                  </div>
                  <div>
                    <span className="text-qcloud-muted block">Depth</span>
                    <span className="font-medium">{fakeHwResult.circuit_depth ?? '—'}</span>
                  </div>
                </div>
                {fakeHwResult.execution_time_ms != null && (
                  <div className="text-[10px] text-qcloud-muted text-center mt-1">
                    Execution: {fakeHwResult.execution_time_ms.toFixed(0)}ms
                  </div>
                )}
              </div>

              {/* Measurement Distribution */}
              {fakeHwResult.measurements && Object.keys(fakeHwResult.measurements).length > 0 && (
                <div className="bg-white rounded-lg p-2 border border-orange-200">
                  <div className="text-[10px] font-medium text-qcloud-text mb-2">Measurement Distribution</div>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {Object.entries(fakeHwResult.measurements)
                      .sort(([, a], [, b]) => b - a)
                      .map(([state, count]) => {
                        const total = Object.values(fakeHwResult.measurements!).reduce((a, b) => a + b, 0)
                        const pct = (count / total) * 100
                        return (
                          <div key={state} className="flex items-center gap-2">
                            <span className="font-mono text-[10px] w-14 text-right shrink-0">|{state}⟩</span>
                            <div className="flex-1 bg-gray-100 rounded-full h-3.5 overflow-hidden">
                              <div
                                className="bg-orange-500 h-3.5 rounded-full transition-all"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className="text-[10px] text-qcloud-muted w-20 text-right shrink-0">
                              {count} ({pct.toFixed(1)}%)
                            </span>
                          </div>
                        )
                      })}
                  </div>
                </div>
              )}
            </div>
          ) : fakeHwResult && !fakeHwResult.success ? (
            <div className="px-4 pb-3 text-xs text-red-600">{fakeHwResult.error}</div>
          ) : null}
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar */}
        <div className="flex-shrink-0 bg-white border-r border-qcloud-border overflow-y-auto" style={{ width: sidebarWidth }}>
          {/* Language Selector (challenge mode, multiple languages) */}
          {challengeMode && publicInfo?.language_whitelist && publicInfo.language_whitelist.length > 1 && (
            <div className="p-3 border-b-2 border-indigo-200 bg-indigo-50">
              <h4 className="text-xs font-semibold text-indigo-700 mb-2 uppercase tracking-wider">
                Language
              </h4>
              <select
                value={selectedLanguage}
                onChange={e => {
                  const lang = e.target.value
                  if (lang === selectedLanguage) return
                  // Save current code as draft
                  localStorage.setItem(`challenge_draft_${homeworkId}_${selectedLanguage}`, code)
                  setSelectedLanguage(lang)
                  // Load draft or default starter for new language
                  const savedDraft = localStorage.getItem(`challenge_draft_${homeworkId}_${lang}`)
                  if (savedDraft) {
                    setCode(savedDraft)
                  } else {
                    const label = lang === 'qiskit' ? 'Qiskit' : lang === 'stim' ? 'Stim' : lang === 'qrisp' ? 'Qrisp' : lang
                    if (lang === 'qrisp') {
                      setCode(`from qrisp import QuantumVariable, h, cx\n\n# Create quantum variable\nqv = QuantumVariable(2)\n\n# Your circuit here:\n\n`)
                    } else if (lang === 'qiskit') {
                      setCode(publicInfo?.starter_code || `qc = QuantumCircuit(2)\n\n# Your circuit here:\n\n`)
                    } else {
                      setCode(`# ${label} code\n\n`)
                    }
                  }
                }}
                className="w-full px-3 py-2 bg-white border-2 border-indigo-400 rounded-lg text-sm font-semibold text-indigo-700 focus:outline-none focus:border-indigo-600 cursor-pointer"
              >
                {publicInfo.language_whitelist.map((lang: string) => (
                  <option key={lang} value={lang}>
                    {lang === 'qiskit' ? 'Qiskit' : lang === 'stim' ? 'Stim' : lang === 'qrisp' ? 'Qrisp' : lang}
                  </option>
                ))}
              </select>
              <p className="mt-1.5 text-[10px] text-indigo-500">
                {selectedLanguage === 'qrisp'
                  ? 'Qrisp compiles to Qiskit automatically. One-way: circuit view is read-only.'
                  : selectedLanguage === 'stim'
                  ? 'Write Stim code for QEC circuits.'
                  : 'Write Qiskit code directly with QuantumCircuit.'}
              </p>
            </div>
          )}

          {/* Authentication: challenge mode = login, homework mode = token */}
          {challengeMode ? (
            <div className="p-3 border-b border-qcloud-border">
              <h4 className="text-xs font-semibold text-qcloud-text mb-2 uppercase tracking-wider">
                Account
              </h4>
              {user ? (
                <>
                  <div className="text-[10px] text-green-600 flex items-center gap-1">
                    <span>Logged in as <strong>{user.username}</strong></span>
                  </div>
                  {!isTokenVerified && (
                    <button
                      onClick={() => handleVerifyToken()}
                      disabled={isVerifying}
                      className="mt-2 w-full px-2 py-1.5 bg-qcloud-primary text-white rounded text-xs hover:bg-qcloud-secondary disabled:opacity-50"
                    >
                      {isVerifying ? 'Connecting...' : 'Connect to Challenge'}
                    </button>
                  )}
                  {isTokenVerified && (
                    <>
                      <div className="mt-1 text-[10px] text-green-600">Connected to challenge</div>
                      {homeworkInfo?.hw_submissions_max != null && (
                        <div className="mt-2 p-2 bg-slate-700/50 rounded text-[10px]">
                          <div className="text-slate-300 font-semibold mb-1">HARDWARE SUBMISSIONS (Platform Key)</div>
                          <div className="flex justify-between text-slate-400">
                            <span>Used:</span>
                            <span className="text-white">{homeworkInfo.hw_submissions_used ?? 0} / {homeworkInfo.hw_submissions_max}</span>
                          </div>
                          <div className="flex justify-between text-slate-400">
                            <span>Remaining:</span>
                            <span className={(homeworkInfo.hw_submissions_remaining ?? 0) > 0 ? 'text-green-400' : 'text-red-400'}>
                              {homeworkInfo.hw_submissions_remaining ?? 0}
                            </span>
                          </div>
                          {(homeworkInfo.hw_submissions_remaining ?? 0) === 0 && (
                            <p className="mt-1 text-[9px] text-amber-400">Use your own IBM API key for unlimited submissions.</p>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </>
              ) : (
                <>
                  <p className="text-[10px] text-qcloud-muted mb-2">
                    Log in to submit circuits and appear on the leaderboard.
                  </p>
                  <div className="flex gap-2">
                    <a
                      href="/login"
                      className="flex-1 px-2 py-1.5 bg-qcloud-primary text-white rounded text-xs text-center hover:bg-qcloud-secondary"
                    >
                      Sign In
                    </a>
                    <a
                      href="/register"
                      className="flex-1 px-2 py-1.5 border border-qcloud-border rounded text-xs text-center hover:bg-gray-50"
                    >
                      Sign Up
                    </a>
                  </div>
                </>
              )}
            </div>
          ) : !isAdmin ? (
          <div className="p-3 border-b border-qcloud-border">
            <h4 className="text-xs font-semibold text-qcloud-text mb-2 uppercase tracking-wider">
              Hardware Token
            </h4>
            <div className="flex gap-1.5">
              <input
                type="text"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleVerifyToken()}
                placeholder="Paste token..."
                className="flex-1 px-2 py-1.5 border border-qcloud-border rounded text-xs font-mono focus:outline-none focus:ring-1 focus:ring-qcloud-primary/50"
              />
              <button
                onClick={() => handleVerifyToken()}
                disabled={isVerifying || !token.trim()}
                className="px-2 py-1.5 bg-qcloud-primary text-white rounded text-xs hover:bg-qcloud-secondary disabled:opacity-50"
              >
                {isVerifying ? '...' : 'OK'}
              </button>
            </div>
            {isTokenVerified && (
              <div className="mt-2 text-[10px] text-green-600 flex items-center gap-1">
                <span>{homeworkInfo?.display_name ? `Welcome, ${homeworkInfo.display_name}` : 'Token verified'}</span>
                <button onClick={handleClearToken} className="text-red-400 hover:text-red-600 ml-auto">(clear)</button>
              </div>
            )}
            {homeworkInfo && !homeworkInfo.valid && (
              <p className="mt-1 text-[10px] text-red-500">{homeworkInfo.error}</p>
            )}
            <p className="mt-1.5 text-[10px] text-qcloud-muted">
              {isToffoli
                ? 'Token is needed to submit your circuit to the leaderboard. Testing is always free.'
                : 'Token is only needed for IBM hardware. The simulator is always free.'}
            </p>
          </div>
          ) : null}

          {/* IBM API Key: required for challenge mode, optional for homework */}
          {!isAdmin && !isToffoli && (
          <div className="p-3 border-b border-qcloud-border">
            <h4 className="text-xs font-semibold text-qcloud-text mb-2 uppercase tracking-wider">
              Your IBM API Key {!challengeMode && <span className="text-qcloud-muted font-normal normal-case">(optional)</span>}
            </h4>
            <input
              type="password"
              value={customApiKey}
              onChange={(e) => {
                setCustomApiKey(e.target.value)
                if (e.target.value) {
                  localStorage.setItem(`hw_custom_apikey_${homeworkId}`, e.target.value)
                } else {
                  localStorage.removeItem(`hw_custom_apikey_${homeworkId}`)
                }
              }}
              placeholder="Paste your IBM API key..."
              className="w-full px-2 py-1.5 border border-qcloud-border rounded text-xs font-mono focus:outline-none focus:ring-1 focus:ring-qcloud-primary/50"
            />
            {customApiKey ? (
              <p className="mt-1.5 text-[10px] text-green-600">
                Your key will be used for hardware submissions. Stored locally only.
              </p>
            ) : (
              <p className="mt-1.5 text-[10px] text-qcloud-muted">
                Provide your own IBM Quantum key to submit jobs using your account. Key is stored in your browser only.
              </p>
            )}
          </div>
          )}

          {/* Student Profile (only when token verified, hidden for admins) */}
          {isTokenVerified && !isAdmin && (
            <div className="p-3 border-b border-qcloud-border">
              <h4 className="text-xs font-semibold text-qcloud-text mb-2 uppercase tracking-wider">
                Leaderboard Profile
              </h4>
              <div className="space-y-2">
                <div>
                  <label className="text-[10px] text-qcloud-muted block mb-0.5">Display Name (shown on leaderboard)</label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Your name or alias..."
                    maxLength={30}
                    className="w-full px-2 py-1.5 border border-qcloud-border rounded text-xs focus:outline-none focus:ring-1 focus:ring-qcloud-primary/50"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-qcloud-muted block mb-0.5">Method Name (your approach)</label>
                  <input
                    type="text"
                    value={methodName}
                    onChange={(e) => setMethodName(e.target.value)}
                    placeholder={isToffoli ? "e.g. decomposition + optimization..." : "e.g. BBPSSW + parity check..."}
                    maxLength={50}
                    className="w-full px-2 py-1.5 border border-qcloud-border rounded text-xs focus:outline-none focus:ring-1 focus:ring-qcloud-primary/50"
                  />
                </div>
                <button
                  onClick={handleSaveProfile}
                  disabled={profileSaving}
                  className="w-full px-2 py-1.5 bg-qcloud-primary text-white rounded text-xs hover:bg-qcloud-secondary disabled:opacity-50 transition-colors"
                >
                  {profileSaving ? 'Saving...' : profileSaved ? 'Saved!' : 'Save Profile'}
                </button>
              </div>
            </div>
          )}

          {/* Queue Status (not relevant for Toffoli) */}
          {!isToffoli && <div className="p-3 border-b border-qcloud-border">
            <h4 className="text-xs font-semibold text-qcloud-text mb-2 uppercase tracking-wider">Queue Status</h4>
            {queueStatus ? (
              <div className="space-y-1.5">
                <div className="flex justify-between text-xs">
                  <span className="text-qcloud-muted">Running</span>
                  <span className="font-medium text-green-600">{queueStatus.total_running}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-qcloud-muted">Queued</span>
                  <span className="font-medium text-amber-600">{queueStatus.total_queued}</span>
                </div>
                {queueStatus.estimated_wait_minutes > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-qcloud-muted">Est. wait</span>
                    <span className="font-medium">~{queueStatus.estimated_wait_minutes}m</span>
                  </div>
                )}
                {/* Individual running/queued jobs - only show YOUR JOBS for students */}
                {isTokenVerified && queueStatus.my_submissions.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-qcloud-border">
                    <p className="text-[10px] font-semibold text-qcloud-primary mb-1">YOUR JOBS</p>
                    {queueStatus.my_submissions.map((s) => (
                      <div key={s.id} className="flex justify-between text-xs bg-qcloud-primary/5 rounded px-2 py-0.5 mb-0.5">
                        <span className="text-qcloud-primary">{s.status === 'queued' ? `#${s.position}` : 'Running'}</span>
                        <span className={s.status === 'running' ? 'text-green-600' : 'text-amber-600'}>{s.status}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-qcloud-muted">Loading...</p>
            )}
          </div>}

          {/* Submission History (only when token verified, hidden for admins and Toffoli) */}
          {isTokenVerified && !isAdmin && !isToffoli && (
            <div className="p-3 border-b border-qcloud-border">
              <h4 className="text-xs font-semibold text-qcloud-text mb-2 uppercase tracking-wider">Submissions</h4>
              {submissions.length === 0 ? (
                <p className="text-xs text-qcloud-muted">No submissions yet.</p>
              ) : (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {submissions.map((sub) => (
                    <Link
                      key={sub.id}
                      to={`${baseUrl}/results/${sub.id}?token=${encodeURIComponent(token)}`}
                      className="block p-1.5 rounded hover:bg-qcloud-bg/50 transition-colors"
                    >
                      <div className="flex justify-between items-center">
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                          sub.status === 'completed' ? 'bg-green-100 text-green-700' :
                          sub.status === 'running' ? 'bg-blue-100 text-blue-700' :
                          sub.status === 'queued' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {sub.status}
                        </span>
                        <span className="text-[10px] text-qcloud-muted">
                          {new Date(sub.created_at).toLocaleTimeString()}
                        </span>
                      </div>
                      {sub.status === 'completed' && sub.fidelity_after != null && (
                        <div className="mt-0.5 flex justify-between text-[10px]">
                          <span className="font-medium text-qcloud-primary">
                            Fidelity: {((sub.fidelity_after) * 100).toFixed(1)}%
                          </span>
                          {sub.success_probability != null && (
                            <span className="text-qcloud-muted">
                              P(success): {((sub.success_probability) * 100).toFixed(1)}%
                            </span>
                          )}
                        </div>
                      )}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Reference Circuit (collapsible) */}
          {publicInfo.reference_circuit && (
            <div className="p-3 border-b border-qcloud-border">
              <button
                onClick={() => setShowRefCircuit(!showRefCircuit)}
                className="text-xs font-semibold text-qcloud-text uppercase tracking-wider flex items-center gap-1 w-full"
              >
                <svg className={`w-3 h-3 transition-transform ${showRefCircuit ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                Reference Circuit
              </button>
              {showRefCircuit && (
                <pre className="mt-2 text-[10px] bg-slate-900 text-slate-300 p-2 rounded max-h-40 overflow-auto font-mono leading-relaxed">
                  {publicInfo.reference_circuit}
                </pre>
              )}
            </div>
          )}

          {/* Problem Description */}
          {publicInfo.description && (
            <div className="p-3">
              <h4 className="text-xs font-semibold text-qcloud-text mb-2 uppercase tracking-wider">Description</h4>
              <div className="max-h-60 overflow-y-auto">
                <MarkdownRenderer content={publicInfo.description} />
              </div>
            </div>
          )}
        </div>

        {/* Resize Handle */}
        <div
          onMouseDown={handleResizeStart}
          className="w-1 flex-shrink-0 bg-transparent hover:bg-qcloud-primary/30 cursor-col-resize transition-colors"
          title="Drag to resize"
        />

        {/* Right: Editor or Composer */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {editorMode === 'code' ? (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* POST_SELECT validation warning for code mode (not for Toffoli) */}
              {!isToffoli && (() => {
                const ancillaInfo = extractMeasurementsFromCode(code)
                const psMatch = code.match(/POST_SELECT\s*=\s*(\{[^}]*\})/)
                if (!psMatch) return null
                const psErrors = validatePostSelectLength(psMatch[1], ancillaInfo)
                if (psErrors.length === 0) {
                  // Show bit order info when valid
                  if (ancillaInfo.ancillaCount > 0) {
                    const bitLabel = ancillaInfo.ancillaClassicalBits.map(b => `c${b}`).join(' ')
                    return (
                      <div className="flex-shrink-0 px-3 py-1 bg-blue-50 border-b border-blue-200 text-[10px] text-blue-700">
                        POST_SELECT bit order (Qiskit convention, highest bit first): [{bitLabel}] — {ancillaInfo.ancillaCount} ancilla bit(s)
                      </div>
                    )
                  }
                  return null
                }
                return (
                  <div className="flex-shrink-0 px-3 py-1 bg-red-50 border-b border-red-200 text-xs text-red-700">
                    <strong>POST_SELECT error:</strong> {psErrors[0]}
                  </div>
                )
              })()}
              <div className="flex-1 min-w-0 min-h-0 overflow-hidden">
                <CodeEditor value={code} onChange={(v) => setCode(v || '')} />
              </div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              <GateToolbar
                onGateDragStart={handleGateDragStart}
                hiddenGroups={isToffoli ? new Set(['Controlled Rotation', 'Swap', 'Multi-Qubit', 'Measurement', 'Dynamic']) : undefined}
                allowedGates={isToffoli ? new Set<GateType>(['X', 'Y', 'Z', 'I', 'H', 'S', 'Sdg', 'T', 'Tdg', 'SX', 'SXdg', 'RX', 'RY', 'RZ', 'P', 'U', 'CX'] as GateType[]) : undefined}
              />
              {/* Circuit controls */}
              <div className="flex-shrink-0 px-4 py-1.5 bg-gray-50 border-b border-qcloud-border flex items-center gap-3 text-xs">
                {isToffoli ? (
                  <>
                    <span className="text-qcloud-muted">Qubits: 3 (A—B—C)</span>
                    <div className="w-px h-4 bg-qcloud-border mx-1" />
                    <span className="text-qcloud-muted">Gates: {circuit.gates.length}</span>
                  </>
                ) : (
                  <>
                    <span className="text-qcloud-muted">Qubits:</span>
                    <button onClick={removeQubit} disabled={circuit.numQubits <= 1}
                      className="w-5 h-5 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-30 flex items-center justify-center text-xs">-</button>
                    <span className="font-medium w-4 text-center">{circuit.numQubits}</span>
                    <button onClick={addQubit} disabled={circuit.numQubits >= 10}
                      className="w-5 h-5 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-30 flex items-center justify-center text-xs">+</button>
                    <div className="w-px h-4 bg-qcloud-border mx-1" />
                    <span className="text-qcloud-muted">Gates: {circuit.gates.length}</span>
                    <div className="w-px h-4 bg-qcloud-border mx-1" />
                    <span className="text-qcloud-muted">POST_SELECT:</span>
                    <input
                      type="text"
                      value={postSelect}
                      onChange={(e) => setPostSelect(e.target.value)}
                      placeholder='None'
                      className={`w-28 px-1.5 py-0.5 border rounded text-xs font-mono bg-white focus:outline-none focus:ring-1 focus:ring-qcloud-primary/50 ${
                        (() => {
                          const info = extractMeasurementsFromCircuit(circuit)
                          const errs = postSelect.trim() ? validatePostSelectLength(postSelect, info) : []
                          return errs.length > 0 ? 'border-red-400' : 'border-qcloud-border'
                        })()
                      }`}
                      title='Post-selection set, e.g. {"00"} or {"00", "11"}. Leave empty for no post-selection (bare Bell pair).'
                    />
                    {!postSelect.trim() && (
                      <span className="text-[10px] text-green-600" title="No post-selection: all shots kept, success probability = 100%">
                        (no filtering)
                      </span>
                    )}
                    {/* Show bit order label */}
                    {(() => {
                      const info = extractMeasurementsFromCircuit(circuit)
                      if (info.ancillaCount === 0) return null
                      const bitLabel = info.ancillaClassicalBits.map(b => `c${b}`).join(' ')
                      const psErrors = postSelect.trim() ? validatePostSelectLength(postSelect, info) : []
                      return (
                        <span className="text-[10px] text-qcloud-muted" title="Qiskit bit order: highest classical bit index first (leftmost)">
                          Bit order: [{bitLabel}]
                          {psErrors.length > 0 && (
                            <span className="text-red-500 ml-1" title={psErrors.join('\n')}>
                              (length must be {info.ancillaCount})
                            </span>
                          )}
                        </span>
                      )
                    })()}
                    <div className="w-px h-4 bg-qcloud-border mx-1" />
                    <span className="text-qcloud-muted">Layout:</span>
                    <input
                      type="text"
                      value={initialLayout}
                      onChange={(e) => setInitialLayout(e.target.value)}
                      placeholder="[0, 1, 2, 3]"
                      className="w-28 px-1.5 py-0.5 border border-qcloud-border rounded text-xs font-mono bg-white focus:outline-none focus:ring-1 focus:ring-qcloud-primary/50"
                      title="INITIAL_LAYOUT: maps logical qubits to physical qubits on hardware. Leave empty for automatic layout."
                    />
                    <span className="text-[10px] text-qcloud-muted" title="Check hardware info for qubit topology and error rates">
                      (optional)
                    </span>
                  </>
                )}
                <button onClick={() => setCircuit(createEmptyCircuit(circuit.numQubits))}
                  className="ml-auto text-red-500 hover:text-red-700 text-xs">Clear</button>
              </div>
              {parseWarning && (
                <div className="flex-shrink-0 px-4 py-1.5 bg-amber-50 border-b border-amber-200 text-xs text-amber-700 flex items-center justify-between">
                  <span>{parseWarning}</span>
                  <button onClick={() => setParseWarning(null)} className="text-amber-500 hover:text-amber-700 ml-2">x</button>
                </div>
              )}
              {/* POST_SELECT length mismatch warning (not for Toffoli) */}
              {!isToffoli && (() => {
                if (!postSelect.trim()) return null
                const info = extractMeasurementsFromCircuit(circuit)
                const psErrors = validatePostSelectLength(postSelect, info)
                if (psErrors.length === 0) return null
                return (
                  <div className="flex-shrink-0 px-4 py-1.5 bg-red-50 border-b border-red-200 text-xs text-red-700">
                    <strong>POST_SELECT error:</strong> {psErrors[0]}
                  </div>
                )
              })()}
              <div className="flex-1 overflow-auto p-4 bg-qcloud-bg">
                <CircuitCanvas
                  circuit={circuit}
                  onDropGate={handleDropGate}
                  onRemoveGate={handleRemoveGate}
                  onMoveGate={handleMoveGate}
                  draggingGate={draggingGate}
                  selectedGates={selectedGates}
                  onSelectionChange={setSelectedGates}
                  highlightedMatches={[]}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Status Bar */}
      <footer className="h-6 flex-shrink-0 bg-qcloud-primary px-4 text-[10px] flex items-center justify-between text-white/80">
        <span>Python - Qiskit</span>
        <div className="flex items-center gap-4">
          <span>{editorMode === 'code' ? 'Code Editor' : 'Circuit Composer'}</span>
          <span>{isToffoli ? 'Unitary Simulator' : selectedBackend}</span>
          <span>UTF-8</span>
        </div>
      </footer>

      {/* Modals for circuit composer */}
      {paramModal.isOpen && paramModal.gateType && (
        <ParameterModal
          isOpen={paramModal.isOpen}
          gateType={paramModal.gateType}
          onConfirm={handleParamConfirm}
          onCancel={() => setParamModal({ isOpen: false, gateType: null, qubitIndex: 0, column: 0 })}
        />
      )}
      {qubitModal.isOpen && qubitModal.gateType && (
        <QubitSelectionModal
          isOpen={qubitModal.isOpen}
          gateType={qubitModal.gateType}
          numQubits={circuit.numQubits}
          onConfirm={handleQubitConfirm}
          onCancel={() => setQubitModal({ isOpen: false, gateType: null, column: 0 })}
        />
      )}
      {conditionalModal.isOpen && (
        <ConditionalGateModal
          isOpen={conditionalModal.isOpen}
          numClassicalBits={circuit.numClassicalBits}
          availableGates={(['X', 'Y', 'Z', 'H', 'S', 'T', 'RX', 'RY', 'RZ'] as GateType[])}
          onConfirm={handleConditionalConfirm}
          onCancel={() => setConditionalModal({ isOpen: false, qubitIndex: 0, column: 0 })}
        />
      )}

      {/* Hardware Info Modal */}
      {showHardwareInfo && <HardwareInfoModal
        selectedBackend={isToffoli ? toffoliBackend : selectedBackend}
        initialLayout={initialLayout}
        code={code}
        editorMode={editorMode}
        isToffoli={isToffoli}
        onClose={() => setShowHardwareInfo(false)}
        onLayoutChange={(newLayout) => {
          setInitialLayout(newLayout)
          // Also update code if in code mode
          if (editorMode === 'code') {
            const layoutLine = `INITIAL_LAYOUT = [${newLayout}]`
            const existingMatch = code.match(/^INITIAL_LAYOUT\s*=\s*\[.*\]$/m)
            if (existingMatch) {
              setCode(code.replace(/^INITIAL_LAYOUT\s*=\s*\[.*\]$/m, layoutLine))
            } else if (newLayout.trim()) {
              const insertPoint = code.indexOf('\nqc = QuantumCircuit')
              if (insertPoint >= 0) {
                setCode(code.slice(0, insertPoint) + '\n' + layoutLine + code.slice(insertPoint))
              } else {
                setCode(code + '\n' + layoutLine + '\n')
              }
            }
          }
        }}
      />}

      {/* Preview Compiled Circuit Modal */}
      {showPreviewCircuit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowPreviewCircuit(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full mx-4 max-h-[80vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-qcloud-border flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="font-bold text-qcloud-text">Full Test Circuit Preview</h3>
                <p className="text-xs text-qcloud-muted mt-0.5">
                  This is the complete circuit that runs on hardware: your circuit + the {evalMethod === 'inverse_bell' ? 'Inverse Bell' : 'Tomography'} test.
                  <span className="text-amber-600 ml-1">(Read-only)</span>
                </p>
              </div>
              <button onClick={() => setShowPreviewCircuit(false)} className="text-qcloud-muted hover:text-red-500 text-lg">&times;</button>
            </div>
            <div className="flex-1 overflow-auto">
              {evalMethod === 'inverse_bell' ? (
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold px-2 py-0.5 bg-teal-50 text-teal-700 rounded">Inverse Bell</span>
                    <span className="text-xs text-qcloud-muted">Single circuit: your code + CX(0,1) + H(0) + measure_all</span>
                  </div>
                  <pre className="text-xs bg-slate-900 text-slate-300 p-4 rounded-lg font-mono leading-relaxed overflow-auto max-h-[50vh] select-all">
                    {generatePreviewCode()}
                  </pre>
                </div>
              ) : (
                <div className="p-4 space-y-4">
                  <p className="text-xs text-qcloud-muted">
                    Tomography runs <strong>3 separate circuits</strong> (one per Pauli basis). Each measures the output qubits in a different basis.
                  </p>
                  {['ZZ', 'XX', 'YY'].map(basis => (
                    <div key={basis}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                          basis === 'ZZ' ? 'bg-blue-50 text-blue-700' :
                          basis === 'XX' ? 'bg-green-50 text-green-700' :
                          'bg-orange-50 text-orange-700'
                        }`}>{basis} Basis</span>
                        <span className="text-xs text-qcloud-muted">
                          {basis === 'ZZ' ? 'Computational basis (no extra gates)' :
                           basis === 'XX' ? 'H(0) + H(1) before measurement' :
                           'Sdg(0) + H(0) + Sdg(1) + H(1) before measurement'}
                        </span>
                      </div>
                      <pre className="text-xs bg-slate-900 text-slate-300 p-4 rounded-lg font-mono leading-relaxed overflow-auto max-h-48 select-all">
                        {generatePreviewCode(basis)}
                      </pre>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Check Transpiled Code Modal */}
      {showTranspileCheck && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowTranspileCheck(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full mx-4 max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-qcloud-border flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="font-bold text-qcloud-text">Transpilation Check</h3>
                <p className="text-xs text-qcloud-muted mt-0.5">
                  Preview how your code will be transpiled for <span className="font-mono font-medium text-blue-600">{selectedBackend}</span>
                </p>
              </div>
              <button onClick={() => setShowTranspileCheck(false)} className="text-qcloud-muted hover:text-red-500 text-lg">&times;</button>
            </div>
            <div className="flex-1 overflow-auto p-4 space-y-4">
              {isCheckingTranspile ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-8 h-8 border-4 border-qcloud-primary border-t-transparent rounded-full animate-spin" />
                  <span className="ml-3 text-sm text-qcloud-muted">Transpiling...</span>
                </div>
              ) : transpileResult ? (
                <>
                  {transpileResult.error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                      {transpileResult.error}
                    </div>
                  )}

                  {/* Parsed Code Info */}
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-qcloud-text mb-2">Parsed Code Info</h4>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-qcloud-muted">INITIAL_LAYOUT:</span>
                        <span className={`font-mono font-medium ${transpileResult.initial_layout ? 'text-green-600' : 'text-slate-400'}`}>
                          {transpileResult.initial_layout ? `[${transpileResult.initial_layout.join(', ')}]` : 'Not set (auto)'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-qcloud-muted">POST_SELECT:</span>
                        <span className={`font-mono font-medium ${transpileResult.post_select ? 'text-purple-600' : 'text-slate-400'}`}>
                          {transpileResult.post_select ? `{${transpileResult.post_select.join(', ')}}` : 'None'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-qcloud-muted">Qubits:</span>
                        <span className="font-mono">{transpileResult.qubit_count ?? '—'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-qcloud-muted">Gates:</span>
                        <span className="font-mono">{transpileResult.gate_count ?? '—'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-qcloud-muted">Depth:</span>
                        <span className="font-mono">{transpileResult.circuit_depth ?? '—'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Transpiled Circuit Info */}
                  {transpileResult.transpiled_depth != null && (
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-qcloud-text mb-2">Transpiled Circuit (optimization_level=3)</h4>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-qcloud-muted">Physical Qubits:</span>
                          <span className="font-mono font-medium text-blue-600">{transpileResult.transpiled_qubit_count}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-qcloud-muted">Transpiled Gates:</span>
                          <span className="font-mono">{transpileResult.transpiled_gate_count}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-qcloud-muted">Transpiled Depth:</span>
                          <span className="font-mono">{transpileResult.transpiled_depth}</span>
                        </div>
                        {transpileResult.physical_qubits && (
                          <div className="flex justify-between col-span-2">
                            <span className="text-qcloud-muted">Qubit Mapping:</span>
                            <span className="font-mono text-green-600">
                              {transpileResult.physical_qubits.map((pq, i) => `q${i}→Q${pq}`).join(', ')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Transpiled QASM */}
                  {transpileResult.transpiled_qasm && (
                    <div>
                      <h4 className="text-sm font-semibold text-qcloud-text mb-2">Transpiled QASM</h4>
                      <pre className="text-xs bg-slate-900 text-slate-300 p-4 rounded-lg font-mono leading-relaxed overflow-auto max-h-[40vh] select-all whitespace-pre">
                        {transpileResult.transpiled_qasm}
                      </pre>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Toffoli Transpile Modal — shows 4 input cases */}
      {showToffoliTranspile && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowToffoliTranspile(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-qcloud-border flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="font-bold text-qcloud-text">Toffoli Transpilation — 4 Input Cases</h3>
                <p className="text-xs text-qcloud-muted mt-0.5">
                  Each test circuit: X gates (input prep) → barrier → your circuit → measure_all, transpiled for <span className="font-mono font-medium text-blue-600">{toffoliBackend}</span>
                </p>
              </div>
              <button onClick={() => setShowToffoliTranspile(false)} className="text-qcloud-muted hover:text-red-500 text-lg">&times;</button>
            </div>
            <div className="flex-1 overflow-auto p-4 space-y-4">
              {isCheckingToffoliTranspile ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-8 h-8 border-4 border-qcloud-primary border-t-transparent rounded-full animate-spin" />
                  <span className="ml-3 text-sm text-qcloud-muted">Transpiling 4 circuits...</span>
                </div>
              ) : toffoliTranspileResult ? (
                <>
                  {toffoliTranspileResult.error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                      {toffoliTranspileResult.error}
                    </div>
                  )}

                  {/* Parsed Code Info */}
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-qcloud-text mb-2">Your Circuit Info</h4>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-qcloud-muted">INITIAL_LAYOUT:</span>
                        <span className={`font-mono font-medium ${toffoliTranspileResult.initial_layout ? 'text-green-600' : 'text-slate-400'}`}>
                          {toffoliTranspileResult.initial_layout ? `[${toffoliTranspileResult.initial_layout.join(', ')}]` : 'Not set (auto)'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-qcloud-muted">Qubits:</span>
                        <span className="font-mono">{toffoliTranspileResult.qubit_count ?? '—'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-qcloud-muted">Gates:</span>
                        <span className="font-mono">{toffoliTranspileResult.gate_count ?? '—'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-qcloud-muted">Depth:</span>
                        <span className="font-mono">{toffoliTranspileResult.circuit_depth ?? '—'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Input case selector tabs */}
                  {toffoliTranspileResult.circuits && (
                    <>
                      <div className="flex gap-2">
                        {(['00', '01', '10', '11'] as const).map(input => {
                          const circ = toffoliTranspileResult.circuits?.[input]
                          const expectedMap: Record<string, string> = { '00': '000', '01': '010', '10': '001', '11': '111' }
                          return (
                            <button
                              key={input}
                              onClick={() => setToffoliTranspileInput(input)}
                              className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
                                toffoliTranspileInput === input
                                  ? 'bg-blue-600 text-white border-blue-600'
                                  : 'bg-white text-qcloud-text border-qcloud-border hover:bg-blue-50'
                              }`}
                            >
                              <div>A={input[0]}, B={input[1]}</div>
                              <div className="text-[10px] opacity-75">→ {expectedMap[input]}</div>
                              {circ && <div className="text-[10px] opacity-75 mt-0.5">depth: {circ.transpiled_depth}, gates: {circ.transpiled_gate_count}</div>}
                            </button>
                          )
                        })}
                      </div>

                      {/* Selected circuit details */}
                      {(() => {
                        const circ = toffoliTranspileResult.circuits[toffoliTranspileInput]
                        if (!circ) return <div className="text-sm text-red-500">No data for input {toffoliTranspileInput}</div>
                        return (
                          <div className="space-y-3">
                            <div className="bg-blue-50 rounded-lg p-4">
                              <h4 className="text-sm font-semibold text-qcloud-text mb-2">
                                Transpiled Circuit for Input A={toffoliTranspileInput[0]}, B={toffoliTranspileInput[1]}
                              </h4>
                              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-qcloud-muted">Physical Qubits:</span>
                                  <span className="font-mono font-medium text-blue-600">{circ.transpiled_qubit_count}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-qcloud-muted">Transpiled Gates:</span>
                                  <span className="font-mono">{circ.transpiled_gate_count}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-qcloud-muted">Transpiled Depth:</span>
                                  <span className="font-mono">{circ.transpiled_depth}</span>
                                </div>
                                {circ.physical_qubits && (
                                  <div className="flex justify-between col-span-2">
                                    <span className="text-qcloud-muted">Qubit Mapping:</span>
                                    <span className="font-mono text-green-600">
                                      {circ.physical_qubits.map((pq: number, i: number) => `q${i}→Q${pq}`).join(', ')}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                            {circ.transpiled_qasm && (
                              <div>
                                <h4 className="text-sm font-semibold text-qcloud-text mb-2">Transpiled QASM</h4>
                                <pre className="text-xs bg-slate-900 text-slate-300 p-4 rounded-lg font-mono leading-relaxed overflow-auto max-h-[40vh] select-all whitespace-pre">
                                  {circ.transpiled_qasm}
                                </pre>
                              </div>
                            )}
                          </div>
                        )
                      })()}
                    </>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Submit Confirmation Modal */}
      {showSubmitModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowSubmitModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-qcloud-border">
              <h3 className="font-bold text-qcloud-text">Confirm Hardware Submission</h3>
              <p className="text-xs text-qcloud-muted mt-1">
                This will submit your circuit to <span className="font-semibold text-blue-600">{selectedBackend}</span> with {shots} shots.
              </p>
            </div>

            <div className="px-6 py-4 space-y-4">
              {/* Budget reminder */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-amber-800">Budget Remaining</span>
                  <span className={`text-sm font-bold ${(homeworkInfo?.budget_remaining_seconds || 0) < 120 ? 'text-red-600' : 'text-amber-700'}`}>
                    {Math.round(homeworkInfo?.budget_remaining_seconds || 0)}s
                  </span>
                </div>
                <div className="w-full bg-amber-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${(homeworkInfo?.budget_remaining_seconds || 0) < 120 ? 'bg-red-500' : 'bg-amber-500'}`}
                    style={{ width: `${Math.max(0, 100 - ((homeworkInfo?.budget_used_seconds || 0) / (homeworkInfo?.budget_total_seconds || 1)) * 100)}%` }}
                  />
                </div>
                <p className="text-[10px] text-amber-600 mt-1">
                  {Math.round(homeworkInfo?.budget_used_seconds || 0)}s used of {homeworkInfo?.budget_total_seconds || 0}s total
                </p>
              </div>

              {/* API Key choice */}
              <div>
                <label className="text-xs font-semibold text-qcloud-text block mb-2">IBM API Key</label>
                <div className="space-y-2">
                  <label className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${submitKeyChoice === 'platform' ? 'border-qcloud-primary bg-blue-50/50' : 'border-qcloud-border hover:bg-gray-50'}`}>
                    <input
                      type="radio"
                      name="apiKeyChoice"
                      checked={submitKeyChoice === 'platform'}
                      onChange={() => setSubmitKeyChoice('platform')}
                      className="mt-0.5"
                    />
                    <div>
                      <span className="text-sm font-medium text-qcloud-text">Use platform key</span>
                      <p className="text-[10px] text-qcloud-muted mt-0.5">Use the course-provided IBM API key. Job time is deducted from your budget.</p>
                    </div>
                  </label>
                  <label className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${submitKeyChoice === 'own' ? 'border-qcloud-primary bg-blue-50/50' : 'border-qcloud-border hover:bg-gray-50'} ${!customApiKey.trim() ? 'opacity-50' : ''}`}>
                    <input
                      type="radio"
                      name="apiKeyChoice"
                      checked={submitKeyChoice === 'own'}
                      onChange={() => { if (customApiKey.trim()) setSubmitKeyChoice('own') }}
                      disabled={!customApiKey.trim()}
                      className="mt-0.5"
                    />
                    <div>
                      <span className="text-sm font-medium text-qcloud-text">Use my own key</span>
                      {customApiKey.trim() ? (
                        <p className="text-[10px] text-green-600 mt-0.5">Your personal IBM key will be used. Key: ****{customApiKey.slice(-4)}</p>
                      ) : (
                        <p className="text-[10px] text-qcloud-muted mt-0.5">No personal key configured. Set it in the sidebar under "Your IBM API Key".</p>
                      )}
                    </div>
                  </label>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-qcloud-border bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => setShowSubmitModal(false)}
                className="px-4 py-2 text-sm text-qcloud-muted hover:text-qcloud-text transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitConfirm}
                className="px-5 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-500 transition-colors"
              >
                Confirm & Submit
              </button>
            </div>
          </div>
        </div>
      )}

      </>
      )}
    </div>
  )
}

function HardwareInfoModal({
  selectedBackend,
  initialLayout,
  code,
  editorMode,
  isToffoli,
  onClose,
  onLayoutChange,
}: {
  selectedBackend: string
  initialLayout: string
  code: string
  editorMode: string
  isToffoli?: boolean
  onClose: () => void
  onLayoutChange: (layout: string) => void
}) {
  const provider = findProviderByBackend(selectedBackend)
  const calibData = getCalibrationData(selectedBackend)

  // Local layout state for the input box, initialized from code/composer state
  const getInitialLayoutStr = () => {
    // Check initialLayout first (set by the Layout button click handler)
    if (initialLayout.trim()) return initialLayout.trim()
    // Fallback: parse code for uncommented INITIAL_LAYOUT lines
    if (editorMode === 'code') {
      const match = code.match(/^[^#\n]*INITIAL_LAYOUT\s*=\s*\[([^\]]*)\]/m)
      if (match) return match[1].trim()
    }
    return ''
  }
  const [localLayout, setLocalLayout] = useState(getInitialLayoutStr)

  // Parse layout string into qubit arrays
  const { layoutQubits, layoutLabels } = useMemo(() => {
    const qubits: number[] = []
    const labels: Record<number, string> = {}
    if (localLayout.trim()) {
      const nums = localLayout.replace(/[\[\]]/g, '').split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n))
      const toffoliLabels = ['A', 'B', 'C']
      nums.forEach((physQ, logQ) => {
        qubits.push(physQ)
        labels[physQ] = isToffoli ? (toffoliLabels[logQ] || String(logQ)) : String(logQ)
      })
    }
    return { layoutQubits: qubits, layoutLabels: labels }
  }, [localLayout, isToffoli])

  // Toffoli layout validation: check linear chain connectivity
  const toffoliLayoutError = useMemo(() => {
    if (!isToffoli || !calibData || layoutQubits.length === 0) return null
    if (layoutQubits.length !== 3) return `Toffoli requires exactly 3 qubits (A, B, C), got ${layoutQubits.length}`
    // Check duplicates
    if (new Set(layoutQubits).size !== 3) return 'Layout has duplicate qubits'
    // Check qubit range
    const maxQ = calibData.qubits.length
    for (const q of layoutQubits) {
      if (q < 0 || q >= maxQ) return `Qubit ${q} is out of range [0, ${maxQ - 1}]`
    }
    // Build edge set from calibration data
    const edgeSet = new Set<string>()
    for (const edge of calibData.edges) {
      edgeSet.add(`${edge.q1}-${edge.q2}`)
      edgeSet.add(`${edge.q2}-${edge.q1}`)
    }
    const [pA, pB, pC] = layoutQubits
    const abConnected = edgeSet.has(`${pA}-${pB}`)
    const bcConnected = edgeSet.has(`${pB}-${pC}`)
    if (!abConnected && !bcConnected) return `Q${pA}(A) ↔ Q${pB}(B) and Q${pB}(B) ↔ Q${pC}(C) are NOT connected`
    if (!abConnected) return `Q${pA}(A) ↔ Q${pB}(B) are NOT connected on this topology`
    if (!bcConnected) return `Q${pB}(B) ↔ Q${pC}(C) are NOT connected on this topology`
    return null // Valid linear chain!
  }, [isToffoli, calibData, layoutQubits])

  const handleApplyLayout = () => {
    onLayoutChange(localLayout)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-qcloud-border flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="font-bold text-qcloud-text">Hardware Details</h3>
            {calibData && (
              <p className="text-xs text-qcloud-muted mt-0.5">
                Calibration: {calibData.calibrationDate.replace('T', ' ').replace('Z', ' UTC')}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-qcloud-muted hover:text-red-500 text-lg">&times;</button>
        </div>
        <div className="overflow-y-auto flex-1">
          {provider ? (
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-lg font-bold text-qcloud-text">{provider.name}</h4>
                  <span className="text-sm text-qcloud-muted">{provider.company}</span>
                </div>
                <span className="text-2xl font-bold text-qcloud-primary">{provider.qubits} qubits</span>
              </div>
              <p className="text-sm text-qcloud-muted leading-relaxed">{provider.description}</p>
              <div className="flex flex-wrap gap-2">
                {provider.features.map((f, i) => (
                  <span key={i} className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium">{f}</span>
                ))}
              </div>

              {/* INITIAL_LAYOUT input */}
              {calibData && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-semibold text-qcloud-text whitespace-nowrap">INITIAL_LAYOUT:</label>
                    <input
                      type="text"
                      value={localLayout}
                      onChange={(e) => setLocalLayout(e.target.value)}
                      placeholder="e.g. 0, 1, 2, 3"
                      className="flex-1 px-3 py-1.5 text-sm font-mono border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                    />
                    <button
                      onClick={handleApplyLayout}
                      className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-500 transition-colors whitespace-nowrap"
                    >
                      Apply to Code
                    </button>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1.5">
                    {isToffoli
                      ? 'Maps A(control), B(control), C(target) to physical qubits. A↔B and B↔C must be directly connected.'
                      : 'Maps logical qubits to physical qubits. Hover qubits on the topology to check error rates, then enter the physical qubit IDs.'}
                  </p>
                  {isToffoli && layoutQubits.length > 0 && (
                    toffoliLayoutError ? (
                      <div className="mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded-md text-xs text-red-700 font-medium">
                        {toffoliLayoutError}
                      </div>
                    ) : (
                      <div className="mt-2 px-3 py-2 bg-green-50 border border-green-200 rounded-md text-xs text-green-700 font-medium">
                        Valid layout: Q{layoutQubits[0]}(A) ↔ Q{layoutQubits[1]}(B) ↔ Q{layoutQubits[2]}(C) — linear chain confirmed
                      </div>
                    )
                  )}
                </div>
              )}

              {/* Hardware Topology Visualization */}
              {calibData && (
                <div className="pt-2">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="text-sm font-semibold text-qcloud-text">Qubit Topology</h5>
                    <span className="text-xs text-qcloud-muted">Hover qubits for error details</span>
                  </div>
                  <HardwareTopology
                    data={calibData}
                    highlightedQubits={layoutQubits}
                    highlightLabels={layoutLabels}
                    width={800}
                    height={460}
                  />
                  {layoutQubits.length > 0 && (
                    <p className="text-xs text-amber-600 mt-2 font-medium">
                      INITIAL_LAYOUT: [{layoutQubits.map((pq, i) => {
                        const label = isToffoli ? (['A', 'B', 'C'][i] || `q${i}`) : `q${i}`
                        return `${label} → Q${pq}`
                      }).join(', ')}]
                    </p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 text-sm pt-2 border-t border-qcloud-border">
                <div>
                  <span className="text-qcloud-muted">Type</span>
                  <p className="font-medium capitalize">{provider.type.replace('_', ' ')}</p>
                </div>
                <div>
                  <span className="text-qcloud-muted">Backend ID</span>
                  <p className="font-mono font-medium">{provider.backendName}</p>
                </div>
                {provider.pricing && (
                  <div>
                    <span className="text-qcloud-muted">Pricing</span>
                    <p className="font-medium">{provider.pricing}</p>
                  </div>
                )}
              </div>
              {provider.docsUrl && (
                <a
                  href={provider.docsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full mt-4 px-4 py-3 bg-blue-600 text-white rounded-lg text-sm font-medium text-center hover:bg-blue-500 transition-colors"
                >
                  View {provider.backendName} on IBM Quantum — topology, error rates, calibration
                </a>
              )}
            </div>
          ) : (
            <div className="p-6 text-center text-qcloud-muted">
              <p className="text-sm">No detailed info available for <span className="font-mono">{selectedBackend}</span></p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default HomeworkPage
